import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, CalendarDays, Users, BarChart3, ClipboardList, CheckSquare,
  FolderKanban, MessageSquare, Megaphone, Bell, FileText, UserCircle, Settings,
  LogOut, Menu, X, Plus, Search, ChevronRight, Clock3, Video, MapPin,
  AlertTriangle, CheckCircle2, Circle, Send, Star, ExternalLink, Save,
  PlayCircle, MoreHorizontal, UserPlus, BookOpen, TrendingUp, Activity,
  RefreshCw, Shield, ArrowLeft, Layers, Trash2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  getMentorDashboard, getMentorStudents, getMentorStudent, getMentorClasses,
  createMentorClass, updateMentorClassStatus, saveMentorAttendance, getMentorAttendance,
  getMentorAssignments, createMentorAssignment, deleteMentorAssignment, getMentorSubmissions,
  reviewMentorSubmission, getMentorProjects, updateMentorProject,
  getMentorMessages, sendMentorMessage, sendMentorAnnouncement,
  getMentorNotifications, markMentorNotificationRead, markAllMentorNotificationsRead, getMentorReports,
  addMentorNote, updateMentorProfile, changePassword, getAdminMentors,
  getAllMentorsOverview, createMentorAccount, assignStudentsBulk, getAdminStudentsWithMentors,
  uploadMentorFile, createMentorProject, deleteMentorProject, isMeetingLinkActive
} from '../../utils/api';

const fmtDate = (value) => value ? new Date(value).toLocaleDateString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric'
}) : '—';

const fmtTime = (value) => value || '—';

const formatMeetingUrl = (link) => {
  if (!link) return '';
  let trimmed = String(link).trim();
  if (!trimmed || trimmed === '#' || trimmed === '/') return '';
  trimmed = trimmed.replace(/^[\/\\]+/, '');
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (/^https?:/i.test(trimmed)) {
    trimmed = trimmed.replace(/^https?:/i, '').replace(/^[\/\\]+/, '');
    return `https://${trimmed}`;
  }
  return `https://${trimmed}`;
};

const initials = (name = '') => name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase() || 'M';

const StatCard = ({ icon, label, value, hint, danger }) => (
  <div className={`mentor-stat-card ${danger ? 'danger' : ''}`}>
    <div className="mentor-stat-icon">{icon}</div>
    <div>
      <div className="mentor-stat-value">{value}</div>
      <div className="mentor-stat-label">{label}</div>
      {hint && <div className="mentor-stat-hint">{hint}</div>}
    </div>
  </div>
);

const ProgressBar = ({ value = 0 }) => (
  <div className="mentor-progress">
    <div style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
  </div>
);

function MentorDashboard() {
  const { user, logout } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'admin';
  const mentorIdFromUrl = searchParams.get('mentorId');

  const [active, setActive] = useState(isAdmin && !mentorIdFromUrl ? 'all-mentors' : 'dashboard');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Admin supervision state
  const [allMentors, setAllMentors] = useState([]);
  const [selectedMentorId, setSelectedMentorId] = useState(mentorIdFromUrl || '');
  const [allOverview, setAllOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Mentor dashboard datasets
  const [data, setData] = useState(null);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [reports, setReports] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);

  // Helper query param builder
  const getParams = () => (isAdmin && selectedMentorId ? { mentorId: selectedMentorId } : {});

  // Load mentors list if user is admin, or load mentor dashboard for regular mentors
  useEffect(() => {
    if (isAdmin) {
      getAdminMentors()
        .then(res => {
          const list = res.data?.data || [];
          setAllMentors(list);
          const initId = mentorIdFromUrl || (list.length > 0 ? list[0]._id : '');
          if (initId) {
            setSelectedMentorId(initId);
          }
        })
        .catch(err => console.error('Failed to load mentors list:', err));

      loadOverview();
    } else {
      loadDashboardData();
    }
  }, [isAdmin]);

  const loadOverview = async () => {
    if (!isAdmin) return;
    setOverviewLoading(true);
    try {
      const res = await getAllMentorsOverview();
      setAllOverview(res.data?.data || null);
    } catch (err) {
      console.error('Failed to load mentors overview:', err);
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadDashboard = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      const res = await getMentorDashboard(getParams());
      setData(res.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to load mentor dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadStudents = async () => {
    try {
      const res = await getMentorStudents(getParams());
      setStudents(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadClasses = async () => {
    try {
      const res = await getMentorClasses(getParams());
      setClasses(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAssignments = async () => {
    try {
      const [a, s] = await Promise.all([
        getMentorAssignments(getParams()),
        getMentorSubmissions(getParams())
      ]);
      setAssignments(a.data?.data || []);
      setSubmissions(s.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await getMentorProjects(getParams());
      setProjects(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadMessages = async () => {
    try {
      const res = await getMentorMessages(getParams());
      setMessages(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadNotifications = async () => {
    try {
      const res = await getMentorNotifications(getParams());
      setNotifications(res.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadReports = async () => {
    try {
      const res = await getMentorReports(getParams());
      setReports(res.data?.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadDashboardData = async (silent = false, customMentorId) => {
    const targetId = customMentorId || selectedMentorId;
    const targetParams = (isAdmin && targetId)
      ? { mentorId: targetId }
      : {};

    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      const [d, s] = await Promise.all([
        getMentorDashboard(targetParams),
        getMentorStudents(targetParams)
      ]);
      const dashData = d.data?.data;
      setData(dashData || null);
      setStudents(s.data?.data || []);
      if (isAdmin && !selectedMentorId && dashData?.mentor?.id) {
        setSelectedMentorId(dashData.mentor.id);
      }
    } catch (err) {
      console.error('Failed to load mentor dashboard data:', err);
      toast.error(err.response?.data?.message || 'Unable to load mentor data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadAll = (silent = false, customId) => loadDashboardData(silent, customId);

  // Re-load when selected mentor or active tab changes
  useEffect(() => {
    if (active === 'all-mentors') {
      loadOverview();
    } else {
      loadDashboardData(false, selectedMentorId);
      if (active === 'schedule' || active === 'attendance') loadClasses();
      if (active === 'assignments') loadAssignments();
      if (active === 'projects') loadProjects();
      if (active === 'messages') loadMessages();
      if (active === 'notifications') loadNotifications();
      if (active === 'reports') loadReports();
    }
  }, [selectedMentorId, active]);

  // Handle mentor switch by Admin
  const handleSwitchMentor = (newMentorId) => {
    setSelectedMentorId(newMentorId);
    setSearchParams({ mentorId: newMentorId });
    if (active === 'all-mentors') {
      setActive('dashboard');
    }
  };

  const openStudent = async (student) => {
    try {
      setSelectedStudent(student);
      setStudentDetail(null);
      const res = await getMentorStudent(student.id, getParams());
      setStudentDetail(res.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to load student');
    }
  };

  const baseNav = [
    ...(isAdmin ? [['all-mentors', 'All Mentors & Activity', Layers]] : []),
    ['dashboard', 'Dashboard', LayoutDashboard],
    ['schedule', 'Schedule & Calendar', CalendarDays],
    ['students', 'Assigned Students', Users],
    ['progress', 'Student Progress', BarChart3],
    ['assignments', 'Assignments', ClipboardList],
    ['attendance', 'Attendance', CheckSquare],
    ['projects', 'Projects', FolderKanban],
    ['messages', 'Messages', MessageSquare],
    ['announcements', 'Announcements', Megaphone],
    ['notifications', 'Notifications', Bell],
    ['reports', 'Reports', FileText],
    ['profile', 'Profile', UserCircle],
    ['settings', 'Settings', Settings]
  ];

  const changePage = (page) => {
    setActive(page);
    setMobileOpen(false);
    if (page === 'all-mentors') loadOverview();
    if (page === 'students') loadStudents();
    if (page === 'schedule') loadClasses();
    if (page === 'assignments') loadAssignments();
    if (page === 'projects') loadProjects();
    if (page === 'messages') loadMessages();
    if (page === 'announcements') loadStudents();
    if (page === 'notifications') loadNotifications();
    if (page === 'reports') loadReports();
  };

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter(s => [s.name, s.email, s.batch, s.course, s.status].join(' ').toLowerCase().includes(q));
  }, [students, search]);

  const activeMentor = !isAdmin
    ? user
    : (allMentors.find(m => m._id === selectedMentorId) || data?.mentor || (allMentors[0] || null));

  const mentor = !isAdmin
    ? (data?.mentor || user || {})
    : (activeMentor || data?.mentor || {});

  const mentorDisplayName = !isAdmin
    ? (user?.name || 'Mentor')
    : (mentor?.name || (allMentors.length === 0 ? 'No Mentors Registered' : 'Select a Mentor'));

  const stats = data?.stats || {};
  const todayClasses = data?.todaysClasses || [];
  const atRisk = (data?.students || []).filter(s => s.status === 'At Risk');



  return (
    <div className="mentor-app">
      {mobileOpen && <div className="mentor-overlay" onClick={() => setMobileOpen(false)} />}
      
      {/* Sidebar */}
      <aside className={`mentor-sidebar ${mobileOpen ? 'open' : ''}`}>
        <div className="mentor-brand">
          <img src="/welogo.png" alt="WeIntern" />
          <span>{isAdmin ? 'Admin · Mentor Portal' : 'Mentor Portal'}</span>
          <button className="mentor-close" onClick={() => setMobileOpen(false)}><X size={20}/></button>
        </div>

        <div className="mentor-profile-mini">
          <div className="mentor-avatar">{initials(mentorDisplayName)}</div>
          <div>
            <strong>{mentorDisplayName}</strong>
            <small>{isAdmin ? (mentor?.email ? `👑 ${mentor.email}` : '👑 Supervised by Admin') : (user?.email || 'Mentor')}</small>
          </div>
        </div>

        <nav className="mentor-nav">
          {baseNav.map(([key, label, Icon]) => (
            <button
              key={key}
              className={active === key ? 'active' : ''}
              onClick={() => changePage(key)}
            >
              <Icon size={18}/>
              <span>{label}</span>
              {key === 'notifications' && stats.unreadNotifications > 0 && <b>{stats.unreadNotifications}</b>}
            </button>
          ))}
        </nav>

        {isAdmin ? (
          <Link to="/admin" className="mentor-logout" style={{ textDecoration: 'none', background: 'rgba(232,168,32,0.15)', color: '#fbd24e', borderColor: '#e8a820' }}>
            <ArrowLeft size={18}/> Return to Admin Panel
          </Link>
        ) : (
          <button className="mentor-logout" onClick={logout}><LogOut size={18}/> Logout</button>
        )}
      </aside>

      {/* Main Content Area */}
      <main className="mentor-main">
        {/* Admin Supervision Topbar Banner */}
        {isAdmin && (
          <div className="admin-mentor-topbar">
            <div className="admin-mentor-topbar-left">
              <span className="admin-badge"><Shield size={13}/> ADMIN SUPERVISION</span>
              <span className="admin-viewing-text">
                Active Mentor: <strong>{mentor?.name || (allMentors.length === 0 ? 'No Mentors Created Yet' : 'None Selected')}</strong>
              </span>
              {mentor?.email && <span className="admin-mentor-email">({mentor.email})</span>}
            </div>

            <div className="admin-mentor-topbar-right">
              {allMentors.length > 0 ? (
                <>
                  <label className="admin-select-label">Switch Mentor:</label>
                  <select
                    className="admin-mentor-dropdown"
                    value={selectedMentorId || ''}
                    onChange={(e) => handleSwitchMentor(e.target.value)}
                  >
                    <option value="">-- Choose Mentor --</option>
                    {allMentors.map(m => (
                      <option key={m._id} value={m._id}>
                        {m.name} ({m.studentCount || 0} students) — {(m.expertise || []).join(', ') || m.email}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <Link to="/admin" className="admin-exit-btn" style={{ background: '#e8a820', color: '#12233f', fontWeight: 800 }}>
                  + Create Mentor in Admin Panel
                </Link>
              )}

              <button
                className="admin-exit-btn"
                onClick={() => changePage('all-mentors')}
                style={{ cursor: 'pointer', background: active === 'all-mentors' ? '#e8a820' : undefined, color: active === 'all-mentors' ? '#12233f' : undefined }}
              >
                <Layers size={14}/> All Mentors Overview
              </button>

              <Link to="/admin" className="admin-exit-btn">
                <ArrowLeft size={14}/> Admin Panel
              </Link>
            </div>
          </div>
        )}

        {/* Regular Header */}
        <header className="mentor-header">
          <div className="mentor-header-left">
            <button className="mentor-menu" onClick={() => setMobileOpen(true)}><Menu size={22}/></button>
            <div>
              <div className="mentor-breadcrumb">
                {isAdmin ? `Admin Mode / Mentors / ${mentor?.name || 'Overview'}` : 'Mentor Portal'} / {baseNav.find(n => n[0] === active)?.[1]}
              </div>
              <h1>
                {active === 'dashboard'
                  ? (isAdmin
                      ? (mentor?.name ? `Dashboard of ${mentor.name}` : 'Mentor Dashboard')
                      : `Welcome, ${user?.name?.split(' ')[0] || user?.name || 'Mentor'} 👋`)
                  : active === 'all-mentors'
                  ? 'All Mentors & Activity Overview'
                  : baseNav.find(n => n[0] === active)?.[1]}
              </h1>
            </div>
          </div>

          <div className="mentor-header-actions">
            <button
              className="mentor-icon-btn"
              onClick={() => {
                if (active === 'all-mentors') loadOverview();
                else loadAll(true, selectedMentorId);
              }}
              title="Refresh"
            >
              <RefreshCw size={18} className={refreshing ? 'spin' : ''}/>
            </button>
            
            <button className="mentor-icon-btn" onClick={() => changePage('notifications')}>
              <Bell size={18}/>
              {stats.unreadNotifications > 0 && <i>{stats.unreadNotifications}</i>}
            </button>

            <div className="mentor-header-user" onClick={() => changePage('profile')}>
              <div className="mentor-avatar small">{initials(isAdmin ? user?.name : mentorDisplayName)}</div>
              <span>{isAdmin ? `${user?.name} (Admin)` : mentorDisplayName}</span>
            </div>
          </div>
        </header>

        {/* Dynamic Page Views */}
        <section className="mentor-content">
          {active === 'all-mentors' && isAdmin && (
            <AllMentorsHub
              overview={allOverview}
              loading={overviewLoading}
              onSelectMentor={handleSwitchMentor}
              selectedMentorId={selectedMentorId}
              onReload={loadOverview}
            />
          )}

          {active === 'dashboard' && (
            loading && !data ? (
              <div className="mentor-loading small">
                <div className="mentor-spinner" />
                <p>Loading mentor dashboard…</p>
              </div>
            ) : (
              <DashboardHome
                data={data}
                onPage={changePage}
                onRefresh={() => loadAll(true, selectedMentorId)}
                openStudent={openStudent}
                isAdmin={isAdmin}
                mentorName={mentor.name}
              />
            )
          )}

          {active === 'schedule' && (
            <SchedulePage
              classes={classes}
              students={students}
              onCreate={() => setModal('class')}
              onReload={loadClasses}
              isAdmin={isAdmin}
              mentorName={mentor.name}
            />
          )}

          {active === 'students' && (
            <StudentsPage
              students={filteredStudents}
              search={search}
              setSearch={setSearch}
              openStudent={openStudent}
              mentorName={mentor.name}
              isAdmin={isAdmin}
            />
          )}

          {active === 'progress' && (
            <ProgressPage
              students={students}
              openStudent={openStudent}
              mentorName={mentor.name}
            />
          )}

          {active === 'assignments' && (
            <AssignmentsPage
              assignments={assignments}
              submissions={submissions}
              students={students}
              onCreate={() => setModal('assignment')}
              onReload={loadAssignments}
              mentorName={mentor.name}
            />
          )}

          {active === 'attendance' && (
            <AttendancePage
              classes={classes}
              students={students}
              onReload={() => loadAll(true, selectedMentorId)}
              mentorName={mentor.name}
            />
          )}

          {active === 'projects' && (
            <ProjectsPage
              projects={projects}
              students={students}
              onReload={loadProjects}
              mentorName={mentor.name}
              selectedMentorId={selectedMentorId}
            />
          )}

          {active === 'messages' && (
            <MessagesPage
              students={students}
              messages={messages}
              onReload={loadMessages}
              mentorName={mentor.name}
              selectedMentorId={selectedMentorId}
              isAdmin={isAdmin}
            />
          )}

          {active === 'announcements' && (
            <AnnouncementsPage
              students={students}
              mentorName={mentor.name}
              selectedMentorId={selectedMentorId}
              isAdmin={isAdmin}
            />
          )}

          {active === 'notifications' && (
            <NotificationsPage
              notifications={notifications}
              onReload={loadNotifications}
              selectedMentorId={selectedMentorId}
            />
          )}

          {active === 'reports' && (
            <ReportsPage
              reports={reports}
              students={students}
              mentorName={mentor.name}
              selectedMentorId={selectedMentorId}
            />
          )}

          {active === 'profile' && (
            <ProfilePage
              mentor={mentor}
              selectedMentorId={selectedMentorId}
              isAdmin={isAdmin}
              onReload={() => loadAll(true, selectedMentorId)}
            />
          )}

          {active === 'settings' && (
            <SettingsPage logout={logout} isAdmin={isAdmin} />
          )}
        </section>
      </main>

      {/* Modals */}
      {modal === 'class' && (
        <ClassModal
          students={students}
          selectedMentorId={selectedMentorId}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadAll(true, selectedMentorId); }}
        />
      )}

      {modal === 'assignment' && (
        <AssignmentModal
          students={students}
          selectedMentorId={selectedMentorId}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); loadAll(true, selectedMentorId); }}
        />
      )}

      {selectedStudent && (
        <StudentModal
          student={selectedStudent}
          detail={studentDetail}
          onClose={() => { setSelectedStudent(null); setStudentDetail(null); }}
          onRefresh={() => openStudent(selectedStudent)}
        />
      )}
    </div>
  );
}

// ── All Mentors Hub (Admin Overview) ────────────────────────
function AllMentorsHub({ overview, loading, onSelectMentor, selectedMentorId, onReload }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [allocatingMentor, setAllocatingMentor] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);

  // Mentor Creation Form State
  const [createForm, setCreateForm] = useState({
    name: '', email: '', password: '', phone: '',
    expertise: '', skills: '', assignedCourses: '', assignedBatches: '',
    bio: '', experience: ''
  });
  const [createSelectedStudents, setCreateSelectedStudents] = useState([]);
  const [createStudentSearch, setCreateStudentSearch] = useState('');
  const [createDomainFilter, setCreateDomainFilter] = useState('all');
  const [creating, setCreating] = useState(false);

  // Allocation Modal State
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [allocationSearch, setAllocationSearch] = useState('');
  const [allocDomainFilter, setAllocDomainFilter] = useState('all');
  const [allocating, setAllocating] = useState(false);

  const loadStudents = async () => {
    setStudentsLoading(true);
    try {
      const res = await getAdminStudentsWithMentors();
      const loaded = res.data?.data || [];
      setAllStudents(loaded);
      return loaded;
    } catch (err) {
      console.error(err);
      return allStudents;
    } finally {
      setStudentsLoading(false);
    }
  };

  useEffect(() => {
    loadStudents();
  }, []);

  // Compute list of unique enrolled domains/courses across students
  const availableDomains = useMemo(() => {
    const set = new Set();
    allStudents.forEach(s => {
      if (s.domain && s.domain !== 'General Internship') set.add(s.domain);
      (s.allDomains || []).forEach(d => {
        if (d && d !== 'General Internship') set.add(d);
      });
      (s.enrolledCourses || []).forEach(c => {
        if (c) set.add(c);
      });
    });
    return Array.from(set).sort();
  }, [allStudents]);

  const toIdString = (ref) => {
    if (!ref) return '';
    if (typeof ref === 'object') return String(ref._id || ref.id || '');
    return String(ref);
  };

  const openAllocateModal = async (mentor) => {
    setAllocatingMentor(mentor);
    const loadedStudents = await loadStudents();
    const targetMentorId = toIdString(mentor);
    const currentAssigned = loadedStudents
      .filter(s => toIdString(s.mentor) === targetMentorId)
      .map(s => toIdString(s));
    setSelectedStudentIds(currentAssigned);
    setAllocationSearch('');
    setAllocDomainFilter('all');
  };

  const handleSaveAllocation = async () => {
    if (!allocatingMentor) return;
    setAllocating(true);
    try {
      const targetMentorId = toIdString(allocatingMentor);
      // Re-fetch the latest assignment state right before diffing so we never
      // compute toAssign/toUnassign against stale data (this previously caused
      // already-assigned students to be unassigned when new ones were added).
      const freshStudents = await loadStudents();
      const currentAssignedIds = freshStudents
        .filter(s => toIdString(s.mentor) === targetMentorId)
        .map(s => toIdString(s));

      const selectedIds = selectedStudentIds.map(id => String(id));
      const toAssign = selectedIds.filter(id => !currentAssignedIds.includes(id));
      const toUnassign = currentAssignedIds.filter(id => !selectedIds.includes(id));

      if (toAssign.length > 0) {
        await assignStudentsBulk({ mentorId: targetMentorId, studentIds: toAssign, action: 'assign' });
      }
      if (toUnassign.length > 0) {
        await assignStudentsBulk({ mentorId: targetMentorId, studentIds: toUnassign, action: 'unassign' });
      }

      toast.success(`Allocated students updated for ${allocatingMentor.name}`);
      setAllocatingMentor(null);
      await loadStudents();
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to allocate students');
    } finally {
      setAllocating(false);
    }
  };

  const handleCreateMentor = async (e) => {
    e.preventDefault();
    if (!createForm.name || !createForm.email || !createForm.password) {
      return toast.error('Name, email and password are required');
    }
    if (createForm.password.length < 6) {
      return toast.error('Password must be at least 6 characters');
    }

    setCreating(true);
    try {
      await createMentorAccount({
        ...createForm,
        studentIds: createSelectedStudents
      });
      toast.success(`Mentor "${createForm.name}" created successfully!`);
      setCreateForm({
        name: '', email: '', password: '', phone: '',
        expertise: '', skills: '', assignedCourses: '', assignedBatches: '',
        bio: '', experience: ''
      });
      setCreateSelectedStudents([]);
      setShowCreateModal(false);
      await loadStudents();
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to create mentor');
    } finally {
      setCreating(false);
    }
  };

  const filteredAllocStudents = allStudents.filter(s => {
    const q = allocationSearch.trim().toLowerCase();
    const matchesSearch = !q || [s.name, s.email, s.college, s.year, s.domain, ...(s.allDomains || []), ...(s.enrolledCourses || [])].join(' ').toLowerCase().includes(q);
    const matchesDomain = allocDomainFilter === 'all' || s.domain === allocDomainFilter || (s.allDomains || []).includes(allocDomainFilter) || (s.enrolledCourses || []).includes(allocDomainFilter);
    return matchesSearch && matchesDomain;
  });

  const filteredCreateStudents = allStudents.filter(s => {
    const q = createStudentSearch.trim().toLowerCase();
    const matchesSearch = !q || [s.name, s.email, s.college, s.year, s.domain, ...(s.allDomains || []), ...(s.enrolledCourses || [])].join(' ').toLowerCase().includes(q);
    const matchesDomain = createDomainFilter === 'all' || s.domain === createDomainFilter || (s.allDomains || []).includes(createDomainFilter) || (s.enrolledCourses || []).includes(createDomainFilter);
    return matchesSearch && matchesDomain;
  });

  if (loading) {
    return (
      <div className="mentor-loading small">
        <div className="mentor-spinner" />
        <p>Loading all mentors activity…</p>
      </div>
    );
  }

  const stats = overview?.stats || {};
  const mentors = overview?.mentors || [];
  const recentAnnouncements = overview?.recentAnnouncements || [];
  const recentClasses = overview?.recentClasses || [];
  const recentAssignments = overview?.recentAssignments || [];

  return (
    <div>
      <div className="mentor-welcome">
        <div>
          <span className="eyebrow">ADMIN SUPERVISION HUB</span>
          <h2>All Mentors & Platform Activity</h2>
          <p>Monitor all mentor operations, active batches, live classes, and announcements.</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            className="primary-btn"
            onClick={() => setShowCreateModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'linear-gradient(135deg, #e8a820, #f5c453)',
              color: '#12233f',
              fontWeight: 800,
              boxShadow: '0 2px 8px rgba(232, 168, 32, 0.3)'
            }}
          >
            <Plus size={16}/> Add New Mentor
          </button>
          <button className="btn btn-outline" onClick={onReload} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={15}/> Refresh Overview
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="mentor-stats-grid">
        <StatCard icon={<Users/>} label="Total Mentors" value={stats.totalMentors || 0} hint="Active mentor accounts" />
        <StatCard icon={<BookOpen/>} label="Students Mentored" value={stats.totalStudentsMentored || 0} hint="Assigned to mentors" />
        <StatCard icon={<CalendarDays/>} label="Total Classes" value={stats.totalClasses || 0} hint="Scheduled sessions" />
        <StatCard icon={<ClipboardList/>} label="Total Assignments" value={stats.totalAssignments || 0} hint="Across all programs" />
        <StatCard icon={<CheckSquare/>} label="Pending Reviews" value={stats.totalPendingSubmissions || 0} hint="Submissions to grade" danger={stats.totalPendingSubmissions > 0} />
      </div>

      {/* Mentors Grid Cards */}
      <div className="mentor-page-title" style={{ marginTop: '1.5rem', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span className="eyebrow">ALL MENTOR ROSTER</span>
          <h3>Active Mentors ({mentors.length})</h3>
        </div>
        <button
          className="btn btn-outline"
          onClick={() => setShowCreateModal(true)}
          style={{ fontSize: '.78rem', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 5 }}
        >
          <UserPlus size={14}/> Create Another Mentor
        </button>
      </div>

      <div className="all-mentors-grid">
        {mentors.map(m => (
          <div
            key={m._id}
            className={`all-mentor-card ${m._id === selectedMentorId ? 'active-mentor' : ''}`}
          >
            <div className="all-mentor-card-head">
              <div className="mentor-avatar">{initials(m.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: '.92rem', display: 'block', color: '#172642' }}>{m.name}</strong>
                <span style={{ fontSize: '.72rem', color: '#7a889b', display: 'block' }}>{m.email}</span>
                <span style={{ fontSize: '.68rem', color: '#e8a820', fontWeight: 700, marginTop: 2, display: 'block' }}>
                  {(m.expertise || []).join(', ') || 'Mentor'}
                </span>
              </div>
              <span className={`status-pill ${m.isBlocked ? 'absent' : 'present'}`}>
                {m.isBlocked ? 'Blocked' : 'Active'}
              </span>
            </div>

            <div className="all-mentor-card-stats">
              <div>
                <strong>{m.studentCount || 0}</strong>
                <span>Students</span>
              </div>
              <div>
                <strong>{m.classCount || 0}</strong>
                <span>Classes</span>
              </div>
              <div>
                <strong>{m.assignmentCount || 0}</strong>
                <span>Assignments</span>
              </div>
              <div>
                <strong style={{ color: m.pendingCount > 0 ? '#dc4545' : '#172642' }}>{m.pendingCount || 0}</strong>
                <span>Pending</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: 4 }}>
              <button
                className="primary-btn"
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => onSelectMentor(m._id)}
              >
                <ExternalLink size={14}/> {m._id === selectedMentorId ? 'Viewing Dashboard ✓' : 'Open Dashboard'}
              </button>
              <button
                className="btn btn-outline"
                style={{
                  fontSize: '.75rem',
                  padding: '7px 11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  fontWeight: 700,
                  borderColor: '#cbd5e1',
                  background: '#f8fafc',
                  color: '#1e293b'
                }}
                onClick={() => openAllocateModal(m)}
                title="Allocate Students to this Mentor"
              >
                <Users size={14}/> Allocate ({m.studentCount || 0})
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── CREATE MENTOR MODAL ── */}
      {showCreateModal && (
        <div className="mentor-modal-backdrop" onClick={() => setShowCreateModal(false)}>
          <div className="mentor-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 740 }}>
            <div className="modal-head">
              <h2>Add New Mentor Account</h2>
              <button onClick={() => setShowCreateModal(false)}><X size={18}/></button>
            </div>
            <form onSubmit={handleCreateMentor} className="modal-body">
              <div className="form-grid">
                <label>
                  Full Name *
                  <input
                    required
                    placeholder="e.g. Dr. Rajesh Kumar"
                    value={createForm.name}
                    onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
                  />
                </label>
                <label>
                  Email Address *
                  <input
                    required
                    type="email"
                    placeholder="e.g. rajesh.kumar@weintern.in"
                    value={createForm.email}
                    onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                  />
                </label>
                <label>
                  Temporary Password * (Min 6 chars)
                  <input
                    required
                    type="password"
                    minLength="6"
                    placeholder="Set secure password"
                    value={createForm.password}
                    onChange={e => setCreateForm({ ...createForm, password: e.target.value })}
                  />
                </label>
                <label>
                  Phone Number
                  <input
                    placeholder="+91 98765 43210"
                    value={createForm.phone}
                    onChange={e => setCreateForm({ ...createForm, phone: e.target.value })}
                  />
                </label>
                <label className="span-2">
                  Expertise & Specialization (comma separated)
                  <input
                    placeholder="e.g. FullStack and MERN stack developer, Data Analytics, Python"
                    value={createForm.expertise}
                    onChange={e => setCreateForm({ ...createForm, expertise: e.target.value })}
                  />
                </label>
                <label>
                  Key Skills (comma separated)
                  <input
                    placeholder="React, Node.js, Express.js, MongoDB"
                    value={createForm.skills}
                    onChange={e => setCreateForm({ ...createForm, skills: e.target.value })}
                  />
                </label>
                <label>
                  Assigned Batches (comma separated)
                  <input
                    placeholder="e.g. Fullstack Batch 15 August 2026"
                    value={createForm.assignedBatches}
                    onChange={e => setCreateForm({ ...createForm, assignedBatches: e.target.value })}
                  />
                </label>
                <label className="span-2">
                  Short Bio / Background
                  <textarea
                    rows={2}
                    placeholder="e.g. 2 years of experience as a fullstack developer..."
                    value={createForm.bio}
                    onChange={e => setCreateForm({ ...createForm, bio: e.target.value })}
                  />
                </label>
              </div>

              {/* Enrolled Students Allocation Section with Domain Filtering */}
              <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <strong style={{ fontSize: '.84rem', color: '#1e293b', display: 'block' }}>
                      Allocate Enrolled Students to this Mentor
                    </strong>
                    <span style={{ fontSize: '.68rem', color: '#64748b' }}>
                      Filter by enrolled domain or course and select students to assign ({createSelectedStudents.length} selected)
                    </span>
                  </div>
                  <div className="search-box" style={{ maxWidth: 240, padding: '5px 9px' }}>
                    <Search size={14}/>
                    <input
                      placeholder="Search by name, domain, email..."
                      value={createStudentSearch}
                      onChange={e => setCreateStudentSearch(e.target.value)}
                      style={{ fontSize: '.72rem' }}
                    />
                  </div>
                </div>

                {/* Domain Filter Bar & Quick Select Controls */}
                <div className="allocation-filter-bar">
                  <select
                    className="allocation-select-filter"
                    value={createDomainFilter}
                    onChange={e => setCreateDomainFilter(e.target.value)}
                  >
                    <option value="all">🎯 All Domains / Courses ({allStudents.length})</option>
                    {availableDomains.map(d => (
                      <option key={d} value={d}>🏷️ Domain: {d}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline"
                    style={{ fontSize: '.72rem', padding: '5px 10px', background: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0', fontWeight: 700 }}
                    onClick={() => {
                      const ids = filteredCreateStudents.map(s => s._id);
                      setCreateSelectedStudents(Array.from(new Set([...createSelectedStudents, ...ids])));
                    }}
                  >
                    + Select All Filtered ({filteredCreateStudents.length})
                  </button>
                  {createSelectedStudents.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      style={{ fontSize: '.72rem', padding: '5px 10px', color: '#b91c1c' }}
                      onClick={() => setCreateSelectedStudents([])}
                    >
                      Clear ({createSelectedStudents.length})
                    </button>
                  )}
                </div>

                <div className="allocation-student-list" style={{ maxHeight: 220 }}>
                  {filteredCreateStudents.length === 0 ? (
                    <div className="mentor-empty">No students found matching this domain/filter</div>
                  ) : (
                    filteredCreateStudents.map(st => {
                      const isSelected = createSelectedStudents.includes(st._id);
                      return (
                        <div
                          key={st._id}
                          className={`allocation-student-item ${isSelected ? 'selected' : ''}`}
                          onClick={() => {
                            if (isSelected) {
                              setCreateSelectedStudents(createSelectedStudents.filter(id => id !== st._id));
                            } else {
                              setCreateSelectedStudents([...createSelectedStudents, st._id]);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                          />
                          <div className="allocation-info">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <strong>{st.name}</strong>
                              <span style={{ fontSize: '.68rem', color: '#64748b' }}>({st.email})</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                              <span className="allocation-domain-tag">
                                🎯 Enrolled Domain: {st.domain || 'General Internship'}
                              </span>
                              {st.college && (
                                <span style={{ fontSize: '.65rem', color: '#475569' }}>🏫 {st.college}</span>
                              )}
                              {st.year && (
                                <span style={{ fontSize: '.65rem', color: '#475569' }}>• {st.year}</span>
                              )}
                              {st.enrolledCourses && st.enrolledCourses.length > 0 && (
                                st.enrolledCourses.map(c => (
                                  <span key={c} className="allocation-course-pill">📚 {c}</span>
                                ))
                              )}
                            </div>
                          </div>
                          {st.mentor ? (
                            <span className="allocation-badge assigned-other">
                              Assigned to {st.mentor.name}
                            </span>
                          ) : (
                            <span className="allocation-badge unassigned">Unassigned</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-btn"
                  disabled={creating}
                  style={{ background: 'linear-gradient(135deg, #e8a820, #f5c453)', color: '#12233f', fontWeight: 800 }}
                >
                  {creating ? 'Creating Mentor...' : `✓ Create Mentor & Allocate (${createSelectedStudents.length} Students)`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── ALLOCATE STUDENTS MODAL ── */}
      {allocatingMentor && (
        <div className="mentor-modal-backdrop" onClick={() => setAllocatingMentor(null)}>
          <div className="mentor-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 740 }}>
            <div className="modal-head">
              <div>
                <h2 style={{ fontSize: '1.05rem', margin: 0 }}>
                  Allocate Students to {allocatingMentor.name}
                </h2>
                <span style={{ fontSize: '.72rem', color: '#64748b' }}>
                  {allocatingMentor.email} • {selectedStudentIds.length} student(s) allocated
                </span>
              </div>
              <button onClick={() => setAllocatingMentor(null)}><X size={18}/></button>
            </div>

            <div className="modal-body">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                <div className="search-box" style={{ flex: 1, minWidth: 200 }}>
                  <Search size={15}/>
                  <input
                    placeholder="Search by student name, domain, email, college..."
                    value={allocationSearch}
                    onChange={e => setAllocationSearch(e.target.value)}
                  />
                </div>
                <select
                  className="allocation-select-filter"
                  value={allocDomainFilter}
                  onChange={e => setAllocDomainFilter(e.target.value)}
                >
                  <option value="all">🎯 All Domains ({allStudents.length})</option>
                  {availableDomains.map(d => (
                    <option key={d} value={d}>🏷️ {d}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-outline"
                  style={{ fontSize: '.75rem', padding: '7px 11px', whiteSpace: 'nowrap', background: '#f0fdf4', color: '#166534', borderColor: '#bbf7d0', fontWeight: 700 }}
                  onClick={() => {
                    const allIds = filteredAllocStudents.map(s => s._id);
                    setSelectedStudentIds(Array.from(new Set([...selectedStudentIds, ...allIds])));
                  }}
                >
                  + Select All in Filter ({filteredAllocStudents.length})
                </button>
                <button
                  type="button"
                  className="btn btn-outline"
                  style={{ fontSize: '.75rem', padding: '7px 11px', whiteSpace: 'nowrap' }}
                  onClick={() => setSelectedStudentIds([])}
                >
                  Clear All
                </button>
              </div>

              <div className="allocation-student-list">
                {studentsLoading ? (
                  <div className="mentor-loading small">
                    <div className="mentor-spinner" />
                    <p>Loading student directory…</p>
                  </div>
                ) : filteredAllocStudents.length === 0 ? (
                  <div className="mentor-empty">No matching students found for this filter</div>
                ) : (
                  filteredAllocStudents.map(st => {
                    const stId = toIdString(st);
                    const targetMentorId = toIdString(allocatingMentor);
                    const isSelected = selectedStudentIds.map(String).includes(stId);
                    const isCurrentMentor = toIdString(st.mentor) === targetMentorId;

                    return (
                      <div
                        key={stId}
                        className={`allocation-student-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedStudentIds(selectedStudentIds.filter(id => String(id) !== stId));
                          } else {
                            setSelectedStudentIds([...selectedStudentIds, stId]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                        />
                        <div className="allocation-info">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <strong>{st.name}</strong>
                            <span style={{ fontSize: '.68rem', color: '#64748b' }}>({st.email})</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                            <span className="allocation-domain-tag">
                              🎯 Domain: {st.domain || 'General Internship'}
                            </span>
                            {st.college && (
                              <span style={{ fontSize: '.65rem', color: '#475569' }}>🏫 {st.college}</span>
                            )}
                            {st.year && (
                              <span style={{ fontSize: '.65rem', color: '#475569' }}>• {st.year}</span>
                            )}
                            {st.enrolledCourses && st.enrolledCourses.length > 0 && (
                              st.enrolledCourses.map(c => (
                                <span key={c} className="allocation-course-pill">📚 {c}</span>
                              ))
                            )}
                          </div>
                        </div>
                        {isCurrentMentor ? (
                          <span className="allocation-badge assigned-this">
                            ✓ Allocated to this mentor
                          </span>
                        ) : st.mentor ? (
                          <span className="allocation-badge assigned-other">
                            Assigned to {st.mentor.name || 'Other Mentor'}
                          </span>
                        ) : (
                          <span className="allocation-badge unassigned">Unassigned</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setAllocatingMentor(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-btn"
                  disabled={allocating}
                  onClick={handleSaveAllocation}
                  style={{ background: 'linear-gradient(135deg, #e8a820, #f5c453)', color: '#12233f', fontWeight: 800 }}
                >
                  {allocating ? 'Saving Allocation...' : `✓ Save Allocation (${selectedStudentIds.length} Students)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Two column: Recent Announcements & Recent Classes */}
      <div className="mentor-grid two" style={{ marginTop: '1.5rem' }}>
        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Recent Announcements Across Mentors</h3>
              <p>Broadcasts and alerts sent to students</p>
            </div>
          </div>
          {recentAnnouncements.length === 0 ? (
            <Empty text="No announcements sent yet." />
          ) : (
            <div>
              {recentAnnouncements.slice(0, 8).map(a => (
                <div key={a._id} className="announcement-feed-card">
                  <div className="announcement-feed-head">
                    <strong>{a.title}</strong>
                    <span>{fmtDate(a.createdAt)}</span>
                  </div>
                  <div className="announcement-feed-body">{a.message}</div>
                  <div style={{ fontSize: '.65rem', color: '#8fa1bd', marginTop: 4 }}>
                    To: {a.recipient?.name || 'Student'} ({a.recipient?.email || ''})
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Recent Scheduled Classes</h3>
              <p>Lectures, workshops and review sessions</p>
            </div>
          </div>
          {recentClasses.length === 0 ? (
            <Empty text="No classes scheduled." />
          ) : (
            <div className="class-list">
              {recentClasses.slice(0, 8).map(c => (
                <div className="class-row" key={c._id}>
                  <div className="class-time">
                    <strong>{fmtTime(c.startTime)}</strong>
                    <span>{fmtDate(c.date)}</span>
                  </div>
                  <div className="class-dot" />
                  <div className="class-info">
                    <strong>{c.title}</strong>
                    <span>By {c.mentor?.name || 'Mentor'} · {c.batch || 'General'}</span>
                  </div>
                  <span className={`status-pill ${c.status}`}>{c.status}</span>
                  {isMeetingLinkActive(c) && formatMeetingUrl(c.meetingLink) ? (
                    <a
                      href={formatMeetingUrl(c.meetingLink)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="join-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Video size={14}/> Join Meeting ↗
                    </a>
                  ) : c.meetingLink ? (
                    <span style={{ fontSize: '.72rem', color: '#94a3b8', padding: '4px 8px' }}>Meeting Ended</span>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function DashboardHome({ data, onPage, openStudent, isAdmin, mentorName }) {
  const stats = data?.stats || {};
  const classes = data?.todaysClasses || [];
  const risk = (data?.students || []).filter(s => s.status === 'At Risk');
  const activity = data?.activity || [];

  return (
    <>
      <div className="mentor-welcome">
        <div>
          <span className="eyebrow">TODAY'S OVERVIEW</span>
          <h2>{isAdmin ? `Dashboard of ${mentorName}` : 'Make every student count.'}</h2>
          <p>Here is what needs attention today for {mentorName || 'this mentor'}.</p>
        </div>
        <div className="mentor-date">
          <CalendarDays size={18}/> {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>

      <div className="mentor-stats-grid">
        <StatCard icon={<Users/>} label="Total Students" value={stats.totalStudents || 0} hint={`${stats.activeStudents || 0} active this week`} />
        <StatCard icon={<Activity/>} label="Active Students" value={stats.activeStudents || 0} hint="Activity in last 7 days" />
        <StatCard icon={<CalendarDays/>} label="Today's Classes" value={stats.todaysClasses || 0} hint="Scheduled for today" />
        <StatCard icon={<ClipboardList/>} label="Pending Reviews" value={stats.pendingAssignments || 0} hint="Submissions awaiting review" />
        <StatCard icon={<TrendingUp/>} label="Average Progress" value={`${stats.averageProgress || 0}%`} hint="Across assigned students" />
        <StatCard icon={<AlertTriangle/>} label="Students At Risk" value={stats.atRiskStudents || 0} hint="Needs attention" danger={stats.atRiskStudents > 0} />
      </div>

      <div className="mentor-grid two">
        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Today's Schedule</h3>
              <p>Classes and mentoring sessions</p>
            </div>
            <button onClick={() => onPage('schedule')} className="text-btn">View calendar <ChevronRight size={16}/></button>
          </div>
          {classes.length === 0 ? (
            <Empty text="No classes scheduled for today." />
          ) : (
            <div className="class-list">
              {classes.map(c => (
                <div className="class-row" key={c._id}>
                  <div className="class-time">
                    <strong>{fmtTime(c.startTime)}</strong>
                    <span>{fmtTime(c.endTime)}</span>
                  </div>
                  <div className="class-dot" />
                  <div className="class-info">
                    <strong>{c.title}</strong>
                    <span>{c.batch || 'Batch'} · {c.students?.length || 0} students</span>
                  </div>
                  <span className={`status-pill ${c.status}`}>{c.status}</span>
                  {isMeetingLinkActive(c) && formatMeetingUrl(c.meetingLink) ? (
                    <a
                      href={formatMeetingUrl(c.meetingLink)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="join-btn"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Video size={14}/> Join Meeting ↗
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Students At Risk</h3>
              <p>Intervene before progress slips</p>
            </div>
            <button onClick={() => onPage('students')} className="text-btn">All students <ChevronRight size={16}/></button>
          </div>
          {risk.length === 0 ? (
            <Empty text="Great! No students are currently at risk." />
          ) : (
            <div className="risk-list">
              {risk.slice(0, 5).map(s => (
                <button className="risk-row" key={s.id} onClick={() => openStudent(s)}>
                  <div className="mentor-avatar tiny">{initials(s.name)}</div>
                  <div>
                    <strong>{s.name}</strong>
                    <span>{s.attendance}% attendance · {s.progress}% progress</span>
                  </div>
                  <AlertTriangle size={17}/>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="mentor-grid two">
        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Learning Activity</h3>
              <p>Study activity recorded this month</p>
            </div>
          </div>
          <MiniActivityChart data={activity} />
        </section>

        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Quick Actions</h3>
              <p>Common mentor tasks</p>
            </div>
          </div>
          <div className="quick-actions">
            <button onClick={() => onPage('schedule')}><CalendarDays/><span>Schedule Class</span><ChevronRight/></button>
            <button onClick={() => onPage('attendance')}><CheckSquare/><span>Take Attendance</span><ChevronRight/></button>
            <button onClick={() => onPage('assignments')}><ClipboardList/><span>Review Assignments</span><ChevronRight/></button>
            <button onClick={() => onPage('students')}><Users/><span>View Students</span><ChevronRight/></button>
          </div>
        </section>
      </div>
    </>
  );
}

function MiniActivityChart({ data }) {
  const max = Math.max(1, ...data.map(x => Number(x.minutes || 0)));
  return (
    <div className="mini-chart">
      {data.slice(-14).map((x, i) => (
        <div className="mini-bar-wrap" key={x._id || i} title={`${x._id}: ${x.minutes} min`}>
          <div className="mini-bar" style={{ height: `${Math.max(6, Number(x.minutes || 0) / max * 100)}%` }} />
          <span>{String(x._id || '').slice(8, 10)}</span>
        </div>
      ))}
      {!data.length && <Empty text="No activity recorded yet." />}
    </div>
  );
}

function SchedulePage({ classes, students, onCreate, onReload, mentorName }) {
  const [view, setView] = useState('week');
  return (
    <div>
      <PageActions
        title={`Schedule & Calendar (${mentorName || 'Mentor'})`}
        subtitle="Manage lectures, workshops, project reviews and one-to-one sessions."
        action="Schedule Class"
        onAction={onCreate}
      />
      <div className="schedule-toolbar">
        <div className="segmented">
          {['day', 'week', 'month'].map(v => (
            <button className={view === v ? 'active' : ''} onClick={() => setView(v)} key={v}>
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <button className="outline-btn" onClick={onReload}><RefreshCw size={15}/> Refresh</button>
      </div>
      <div className="mentor-card">
        <div className="calendar-heading">
          <h3>{view === 'day' ? 'Daily Schedule' : view === 'week' ? 'Weekly Schedule' : 'Monthly Schedule'}</h3>
          <span>{classes.length} classes</span>
        </div>
        <div className="calendar-grid">
          {classes.map(c => (
            <div className="calendar-event" key={c._id}>
              <div className="event-date">{fmtDate(c.date)}</div>
              <strong>{c.title}</strong>
              <span>{c.startTime} – {c.endTime}</span>
              <span>{c.batch || 'All assigned students'} · {c.students?.length || 0} students</span>
              <div className="event-actions">
                <span className={`status-pill ${c.status}`}>{c.status}</span>
                {isMeetingLinkActive(c) && formatMeetingUrl(c.meetingLink) ? (
                  <a
                    href={formatMeetingUrl(c.meetingLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Video size={14}/> Join Meeting ↗
                  </a>
                ) : c.meetingLink ? (
                  <span style={{ fontSize: '.68rem', color: '#94a3b8', fontStyle: 'italic' }}>Meeting Ended</span>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {['live', 'completed', 'cancelled'].map(status => (
                  <button
                    key={status}
                    className="mini-action"
                    onClick={async () => {
                      try {
                        await updateMentorClassStatus(c._id, status);
                        toast.success(`Class marked ${status}`);
                        onReload();
                      } catch (e) {
                        toast.error(e.response?.data?.message || 'Unable to update class');
                      }
                    }}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {!classes.length && <Empty text="No classes scheduled yet." />}
        </div>
      </div>
    </div>
  );
}

function StudentsPage({ students, search, setSearch, openStudent, mentorName }) {
  return (
    <div>
      <PageActions
        title={`Assigned Students (${mentorName || 'Mentor'})`}
        subtitle={`Students assigned to ${mentorName || 'this mentor'}.`}
      />
      <div className="mentor-card">
        <div className="filter-bar">
          <div className="search-box">
            <Search size={17}/>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, batch or course..."
            />
          </div>
          <span>{students.length} students</span>
        </div>
        <div className="table-wrap">
          <table className="mentor-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Batch</th>
                <th>Attendance</th>
                <th>Progress</th>
                <th>Assignments</th>
                <th>Last Activity</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {students.map(s => (
                <tr key={s.id}>
                  <td>
                    <button className="person-cell" onClick={() => openStudent(s)}>
                      <div className="mentor-avatar tiny">{initials(s.name)}</div>
                      <span>
                        <strong>{s.name}</strong>
                        <small>{s.email}</small>
                      </span>
                    </button>
                  </td>
                  <td>{s.batch}</td>
                  <td><strong>{s.attendance}%</strong></td>
                  <td>
                    <div className="table-progress">
                      <ProgressBar value={s.progress}/>
                      <span>{s.progress}%</span>
                    </div>
                  </td>
                  <td>{s.assignments || 0}</td>
                  <td>{s.lastActivity ? fmtDate(s.lastActivity) : 'No activity'}</td>
                  <td>
                    <span className={`risk-badge ${s.status.toLowerCase().replace(' ', '-')}`}>
                      {s.status}
                    </span>
                  </td>
                  <td>
                    <button className="icon-link" onClick={() => openStudent(s)}>
                      <ChevronRight size={17}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!students.length && <Empty text="No students assigned yet." />}
        </div>
      </div>
    </div>
  );
}

function ProgressPage({ students, openStudent, mentorName }) {
  return (
    <div>
      <PageActions
        title={`Student Progress Overview (${mentorName || 'Mentor'})`}
        subtitle="Compare progress, attendance and engagement across assigned students."
      />
      <div className="mentor-progress-cards">
        {students.map(s => (
          <button className="progress-student" key={s.id} onClick={() => openStudent(s)}>
            <div className="person-cell">
              <div className="mentor-avatar tiny">{initials(s.name)}</div>
              <span>
                <strong>{s.name}</strong>
                <small>{s.course} · {s.batch}</small>
              </span>
            </div>
            <div className="metric-line">
              <span>Overall Progress</span>
              <b>{s.progress}%</b>
            </div>
            <ProgressBar value={s.progress}/>
            <div className="progress-meta">
              <span>Attendance {s.attendance}%</span>
              <span>{s.studyMinutes || 0} min study</span>
            </div>
            <div className="metric-line">
              <span>Status</span>
              <span className={`risk-badge ${s.status.toLowerCase().replace(' ', '-')}`}>{s.status}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function AssignmentsPage({ assignments, submissions, students, onCreate, onReload, mentorName }) {
  const [review, setReview] = useState(null);

  const handleDeleteAssignment = async (id, title) => {
    if (!window.confirm(`Are you sure you want to delete assignment "${title}"?\n\nThis will remove the assignment and all student submissions for it.`)) return;
    try {
      await deleteMentorAssignment(id);
      toast.success(`Assignment "${title}" deleted successfully`);
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete assignment');
    }
  };

  return (
    <div>
      <PageActions
        title={`Assignments & Reviews (${mentorName || 'Mentor'})`}
        subtitle="Create assignments and review student submissions."
        action="Create Assignment"
        onAction={onCreate}
      />
      <div className="mentor-grid two">
        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Assignments Created</h3>
              <p>Deadlines and submission status</p>
            </div>
            <button className="outline-btn" onClick={onReload}><RefreshCw size={15}/></button>
          </div>
          <div className="assignment-list">
            {assignments.map(a => (
              <div className="assignment-row" key={a._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                  <div className="assignment-icon"><ClipboardList/></div>
                  <div style={{ minWidth: 0 }}>
                    <strong style={{ display: 'block', color: '#172642' }}>{a.title}</strong>
                    <span style={{ fontSize: '.75rem', color: '#64748b' }}>{a.batch || 'Assigned students'} · Due {fmtDate(a.dueDate)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div className="assignment-stats">
                    <b>{a.submitted}</b><small>submitted</small>
                    <b>{a.pending}</b><small>pending</small>
                    <b>{a.averageScore}%</b><small>avg</small>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteAssignment(a._id, a.title)}
                    style={{
                      background: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fca5a5',
                      borderRadius: '6px',
                      padding: '5px 8px',
                      fontSize: '.72rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.15s ease'
                    }}
                    title="Delete Assignment"
                  >
                    <Trash2 size={13}/> Delete
                  </button>
                </div>
              </div>
            ))}
            {!assignments.length && <Empty text="No assignments created yet." />}
          </div>
        </section>

        <section className="mentor-card">
          <div className="mentor-card-head">
            <div>
              <h3>Submission Queue</h3>
              <p>Review work submitted by students</p>
            </div>
          </div>
          <div className="submission-list">
            {submissions.map(s => (
              <button className="submission-row" key={s._id} onClick={() => setReview(s)}>
                <div className="mentor-avatar tiny">{initials(s.student?.name)}</div>
                <div>
                  <strong>{s.student?.name}</strong>
                  <span>{s.assignment?.title}</span>
                </div>
                <span className={`status-pill ${s.status}`}>{s.status}</span>
                <ChevronRight size={16}/>
              </button>
            ))}
            {!submissions.length && <Empty text="No submissions waiting for review." />}
          </div>
        </section>
      </div>

      {review && (
        <ReviewModal
          submission={review}
          onClose={() => setReview(null)}
          onSaved={() => { setReview(null); onReload(); }}
        />
      )}
    </div>
  );
}

function AttendancePage({ classes, students, onReload, mentorName }) {
  const [classId, setClassId] = useState('');
  const [records, setRecords] = useState({});
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyFilter, setHistoryFilter] = useState('all');
  const [isSavedForSelected, setIsSavedForSelected] = useState(false);
  const [saving, setSaving] = useState(false);

  const selected = classes.find(c => c._id === classId);

  useEffect(() => {
    if (classes.length && !classId) setClassId(classes[0]._id);
  }, [classes, classId]);

  // Load all attendance history records
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await getMentorAttendance();
      setHistory(res.data?.data || []);
    } catch (e) {
      // Ignore or empty
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  // When classId changes, check if attendance was already recorded for this class
  useEffect(() => {
    let isMounted = true;
    const fetchClassAttendance = async () => {
      if (!selected) return;
      try {
        const res = await getMentorAttendance({ classId: selected._id });
        const existingRecords = res.data?.data || [];
        if (!isMounted) return;

        const next = {};
        if (existingRecords.length > 0) {
          setIsSavedForSelected(true);
          existingRecords.forEach(r => {
            const sId = String(r.student?._id || r.student || '');
            if (sId) next[sId] = r.status;
          });
          // For any students in class not in existing records, default to present
          (selected.students || []).forEach(s => {
            const sId = String(s._id || s.id || s);
            if (!next[sId]) next[sId] = 'present';
          });
        } else {
          setIsSavedForSelected(false);
          (selected.students || []).forEach(s => {
            const sId = String(s._id || s.id || s);
            next[sId] = 'present';
          });
        }
        setRecords(next);
      } catch (e) {
        if (isMounted) {
          const next = {};
          (selected.students || []).forEach(s => {
            const sId = String(s._id || s.id || s);
            next[sId] = 'present';
          });
          setRecords(next);
        }
      }
    };

    fetchClassAttendance();
    return () => { isMounted = false; };
  }, [classId, selected]);

  const save = async () => {
    if (!selected) return toast.error('Select a class');
    setSaving(true);
    try {
      await saveMentorAttendance({
        classId,
        records: Object.entries(records).map(([studentId, status]) => ({ studentId, status }))
      });
      toast.success('Attendance saved successfully');
      setIsSavedForSelected(true);
      await loadHistory();
      onReload(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Unable to save attendance');
    } finally {
      setSaving(false);
    }
  };

  // Filtered attendance history
  const filteredHistory = history.filter(item => {
    const q = historySearch.trim().toLowerCase();
    const stName = item.student?.name || '';
    const stEmail = item.student?.email || '';
    const clsTitle = item.classId?.title || '';
    const matchesSearch = !q || [stName, stEmail, clsTitle, item.status].join(' ').toLowerCase().includes(q);
    const matchesFilter = historyFilter === 'all' || item.status === historyFilter;
    return matchesSearch && matchesFilter;
  });

  const totalLogs = history.length;
  const presentCount = history.filter(h => h.status === 'present').length;
  const lateCount = history.filter(h => h.status === 'late').length;
  const absentCount = history.filter(h => h.status === 'absent').length;

  return (
    <div>
      <PageActions
        title={`Attendance Management & Records (${mentorName || 'Mentor'})`}
        subtitle="Mark live session attendance and view verified attendance records."
      />

      {/* Attendance Stats Cards */}
      <div className="mentor-stats-grid" style={{ marginBottom: '1.25rem' }}>
        <StatCard icon={<CheckSquare/>} label="Total Attendance Logs" value={totalLogs} hint="Recorded student sessions" />
        <StatCard icon={<CheckCircle2/>} label="Present Rate" value={totalLogs ? `${Math.round((presentCount / totalLogs) * 100)}%` : '0%'} hint={`${presentCount} marked present`} />
        <StatCard icon={<Clock3/>} label="Late Rate" value={totalLogs ? `${Math.round((lateCount / totalLogs) * 100)}%` : '0%'} hint={`${lateCount} marked late`} />
        <StatCard icon={<AlertTriangle/>} label="Absent Rate" value={totalLogs ? `${Math.round((absentCount / totalLogs) * 100)}%` : '0%'} hint={`${absentCount} marked absent`} danger={absentCount > 0} />
      </div>

      {/* Attendance Marking Card */}
      <div className="mentor-card" style={{ marginBottom: '1.5rem' }}>
        <div className="mentor-card-head" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '12px', marginBottom: '14px' }}>
          <div>
            <h3 style={{ fontSize: '1rem', margin: 0 }}>Mark Session Attendance</h3>
            <span style={{ fontSize: '.75rem', color: '#64748b' }}>
              {selected ? `${selected.title} (${fmtDate(selected.date)})` : 'Select a scheduled class'}
            </span>
          </div>
          {isSavedForSelected && (
            <span style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '3px 10px', borderRadius: '12px', fontSize: '.72rem', fontWeight: 700 }}>
              ✓ Attendance Saved
            </span>
          )}
        </div>

        <div className="attendance-toolbar">
          <select value={classId} onChange={e => setClassId(e.target.value)} style={{ minWidth: 260 }}>
            <option value="">Select class to mark attendance</option>
            {classes.map(c => (
              <option value={c._id} key={c._id}>
                {fmtDate(c.date)} · {c.title}
              </option>
            ))}
          </select>
          <button
            className="outline-btn"
            type="button"
            onClick={() => {
              if (selected) {
                const n = {};
                (selected.students || []).forEach(s => {
                  const sId = String(s._id || s.id || s);
                  n[sId] = 'present';
                });
                setRecords(n);
              }
            }}
          >
            Mark All Present
          </button>
          <button className="primary-btn" disabled={saving} onClick={save}>
            <Save size={15}/> {saving ? 'Saving...' : 'Save Attendance'}
          </button>
        </div>

        {!selected ? (
          <Empty text="Select a class from the dropdown above to take or view attendance." />
        ) : (
          <div className="table-wrap">
            <table className="mentor-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Present</th>
                  <th>Late</th>
                  <th>Absent</th>
                  <th>Excused</th>
                </tr>
              </thead>
              <tbody>
                {(selected.students || []).map(s => {
                  const sId = String(s._id || s.id || s);
                  return (
                    <tr key={sId}>
                      <td>
                        <div className="person-cell">
                          <div className="mentor-avatar tiny">{initials(s.name || 'Student')}</div>
                          <span><strong>{s.name || 'Student'}</strong><small>{s.email || ''}</small></span>
                        </div>
                      </td>
                      {['present', 'late', 'absent', 'excused'].map(status => (
                        <td key={status}>
                          <button
                            type="button"
                            className={`attendance-radio ${records[sId] === status ? 'selected' : ''}`}
                            onClick={() => setRecords(r => ({ ...r, [sId]: status }))}
                          >
                            <span/>
                          </button>
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Attendance History & Saved Log Table (Where Attendance is Stored & Checked) */}
      <div className="mentor-card">
        <div className="mentor-card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h3 style={{ fontSize: '1rem', margin: 0 }}>Attendance Records & Saved History</h3>
            <span style={{ fontSize: '.75rem', color: '#64748b' }}>
              All stored attendance logs across your mentorship sessions ({history.length} total entries)
            </span>
          </div>
          <button className="outline-btn small" onClick={loadHistory} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <RefreshCw size={13}/> Refresh History
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, margin: '14px 0', flexWrap: 'wrap' }}>
          <div className="search-box" style={{ flex: 1, minWidth: 220 }}>
            <Search size={14}/>
            <input
              placeholder="Search by student, class, or status..."
              value={historySearch}
              onChange={e => setHistorySearch(e.target.value)}
              style={{ fontSize: '.78rem' }}
            />
          </div>
          <select
            className="allocation-select-filter"
            value={historyFilter}
            onChange={e => setHistoryFilter(e.target.value)}
          >
            <option value="all">🎯 All Statuses ({history.length})</option>
            <option value="present">🟢 Present ({presentCount})</option>
            <option value="late">🟠 Late ({lateCount})</option>
            <option value="absent">🔴 Absent ({absentCount})</option>
            <option value="excused">⚪ Excused</option>
          </select>
        </div>

        {historyLoading ? (
          <div className="mentor-loading small">
            <div className="mentor-spinner" />
            <p>Loading attendance records…</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <Empty text={history.length === 0 ? "No attendance has been recorded yet. Select a class above and click Save Attendance." : "No records match your search."} />
        ) : (
          <div className="table-wrap">
            <table className="mentor-table">
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Class / Session</th>
                  <th>Student</th>
                  <th>Status</th>
                  <th>Recorded At</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map(item => {
                  const statusColors = {
                    present: { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
                    late: { bg: '#fffbeb', text: '#b45309', border: '#fde68a' },
                    absent: { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' },
                    excused: { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' }
                  };
                  const color = statusColors[item.status] || statusColors.present;

                  return (
                    <tr key={item._id}>
                      <td style={{ fontSize: '.78rem', color: '#1e293b', fontWeight: 600 }}>
                        {item.classId?.date ? fmtDate(item.classId.date) : fmtDate(item.markedAt)}
                        {item.classId?.startTime && (
                          <small style={{ display: 'block', color: '#64748b' }}>{item.classId.startTime}–{item.classId.endTime}</small>
                        )}
                      </td>
                      <td>
                        <strong style={{ fontSize: '.84rem', color: '#1e293b', display: 'block' }}>
                          {item.classId?.title || 'Mentor Class Session'}
                        </strong>
                      </td>
                      <td>
                        <div className="person-cell">
                          <div className="mentor-avatar tiny">{initials(item.student?.name || 'Student')}</div>
                          <span>
                            <strong>{item.student?.name || 'Student'}</strong>
                            <small>{item.student?.email || ''}</small>
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          background: color.bg,
                          color: color.text,
                          border: `1px solid ${color.border}`,
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '.72rem',
                          fontWeight: 700,
                          textTransform: 'capitalize',
                          display: 'inline-block'
                        }}>
                          {item.status === 'present' ? '🟢 Present' : item.status === 'late' ? '🟠 Late' : item.status === 'absent' ? '🔴 Absent' : '⚪ Excused'}
                        </span>
                      </td>
                      <td style={{ fontSize: '.72rem', color: '#64748b' }}>
                        {new Date(item.markedAt || item.createdAt || item.updatedAt).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ProjectsPage({ projects, students = [], onReload, mentorName, selectedMentorId }) {
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete project "${title}"? This cannot be undone.`)) return;
    try {
      await deleteMentorProject(id);
      toast.success('Project deleted');
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete project');
    }
  };

  return (
    <div>
      <PageActions
        title={`Internship Projects (${mentorName || 'Mentor'})`}
        subtitle="Create, assign, and track capstone projects allocated to students."
        action="+ Add Project"
        actionText="+ Add Project"
        onAction={() => setCreating(true)}
      />
      <div className="project-grid">
        {projects.map(p => (
          <div className="project-card" key={p._id} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div className="project-head">
                <div className="mentor-avatar tiny">{initials(p.student?.name)}</div>
                <div>
                  <strong>{p.student?.name}</strong>
                  <small style={{ fontWeight: 700, color: '#1e293b' }}>{p.title}</small>
                </div>
                <span className={`status-pill ${p.status}`}>
                  {p.status === 'submitted' ? '⏳ Submitted' : p.status || 'assigned'}
                </span>
              </div>

              {p.score !== undefined && p.score !== null && (
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '2px 8px', borderRadius: '15px', fontWeight: 800, fontSize: '.7rem' }}>
                    Grade: {p.score}/100
                  </span>
                </div>
              )}

              <ProgressBar value={p.progress}/>
              <div className="project-meta">
                <span>{p.progress}% complete</span>
                <span>{fmtDate(p.lastUpdate || p.updatedAt)}</span>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '8px 0' }}>
                {p.githubUrl && (
                  <a href={p.githubUrl} target="_blank" rel="noreferrer" className="project-link" style={{ margin: 0 }}>
                    <ExternalLink size={13}/> Repository
                  </a>
                )}
                {p.liveDemoUrl && (
                  <a href={p.liveDemoUrl} target="_blank" rel="noreferrer" className="project-link" style={{ margin: 0, color: '#0284c7' }}>
                    <ExternalLink size={13}/> Live Demo
                  </a>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
              <button
                className={p.status === 'submitted' ? 'primary-btn' : 'outline-btn'}
                style={{ flex: 1, justifyContent: 'center' }}
                onClick={() => setEditing(p)}
              >
                {p.status === 'submitted' ? '🔍 Review & Grade' : '✏️ Review / Update'}
              </button>
              <button className="danger-btn" style={{ marginTop: 0, padding: '9px 12px' }} onClick={() => handleDelete(p._id, p.title)}>
                Delete
              </button>
            </div>
          </div>
        ))}
        {!projects.length && <Empty text="No projects assigned yet. Click + Assign Project to allocate projects to students." />}
      </div>
      {editing && (
        <ProjectModal
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }}
        />
      )}
      {creating && (
        <CreateProjectModal
          students={students}
          selectedMentorId={selectedMentorId}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); onReload(); }}
        />
      )}
    </div>
  );
}

function CreateProjectModal({ students = [], selectedMentorId, onClose, onSaved }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggleStudent = (id) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    if (selectedStudentIds.length === students.length) setSelectedStudentIds([]);
    else setSelectedStudentIds(students.map(s => s.id));
  };

  const submit = async () => {
    if (!title.trim()) return toast.error('Project title is required');
    if (selectedStudentIds.length === 0) return toast.error('Select at least one student to assign the project to');
    setSubmitting(true);
    try {
      await createMentorProject({
        title: title.trim(),
        description: description.trim(),
        githubUrl: githubUrl.trim(),
        studentIds: selectedStudentIds,
        mentorId: selectedMentorId
      });
      toast.success('Project allocated to students successfully');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to assign project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title="Create & Assign Project" onClose={onClose}>
      <div className="form-grid">
        <label className="span-2">
          Project Title *
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. AI Recommendation Engine"/>
        </label>
        <label className="span-2">
          GitHub Starter Repository (Optional)
          <input value={githubUrl} onChange={e => setGithubUrl(e.target.value)} placeholder="https://github.com/..."/>
        </label>
        <label className="span-2">
          Description & Objectives
          <textarea rows="4" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe project deliverables, tech stack, and evaluation criteria..."/>
        </label>
        <div className="span-2">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong className="form-heading" style={{ margin: 0 }}>Assign to Students ({selectedStudentIds.length}/{students.length})</strong>
            {students.length > 0 && (
              <button type="button" onClick={handleSelectAll} className="text-btn">
                {selectedStudentIds.length === students.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          <div className="checkbox-grid" style={{ maxHeight: 180 }}>
            {students.map(s => (
              <label key={s.id}>
                <input
                  type="checkbox"
                  checked={selectedStudentIds.includes(s.id)}
                  onChange={() => toggleStudent(s.id)}
                />
                {s.name} ({s.domain || 'General'})
              </label>
            ))}
            {students.length === 0 && (
              <div style={{ color: '#94a3b8', fontSize: '.75rem', padding: '10px' }}>
                No students allocated to this mentor yet.
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="modal-actions">
        <button className="outline-btn" onClick={onClose}>Cancel</button>
        <button className="primary-btn" onClick={submit} disabled={submitting}>
          <Layers size={15}/> {submitting ? 'Assigning...' : 'Assign Project'}
        </button>
      </div>
    </Modal>
  );
}

function MessagesPage({ students, messages, onReload, mentorName, selectedMentorId, isAdmin }) {
  const [recipient, setRecipient] = useState('');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [attachmentUrl, setAttachmentUrl] = useState('');

  const send = async () => {
    if (!recipient || !message.trim()) return toast.error('Select a student and enter a message');
    try {
      await sendMentorMessage({
        recipientId: recipient,
        subject,
        message,
        attachmentUrl,
        mentorId: selectedMentorId
      });
      setMessage('');
      setSubject('');
      setAttachmentUrl('');
      toast.success('Message sent');
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to send message');
    }
  };

  return (
    <div>
      <PageActions
        title={`Student Messages (${mentorName || 'Mentor'})`}
        subtitle="Direct communication with assigned students."
      />
      <div className="mentor-grid two">
        <section className="mentor-card">
          <h3>New Message</h3>
          <div className="form-grid">
            <label>
              Student
              <select value={recipient} onChange={e => setRecipient(e.target.value)}>
                <option value="">Select student</option>
                {students.map(s => (
                  <option value={s.id} key={s.id}>{s.name} · {s.email}</option>
                ))}
              </select>
            </label>
            <label>
              Subject
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"/>
            </label>
            <label className="span-2">
              Attachment Link
              <input value={attachmentUrl} onChange={e => setAttachmentUrl(e.target.value)} placeholder="Drive / GitHub / file URL"/>
            </label>
            <label className="span-2">
              Message
              <textarea rows="6" value={message} onChange={e => setMessage(e.target.value)} placeholder="Write your message..."/>
            </label>
          </div>
          <button className="primary-btn" onClick={send}><Send size={15}/> Send Message</button>
        </section>

        <section className="mentor-card">
          <div className="mentor-card-head">
            <h3>Recent Messages</h3>
            <button className="outline-btn" onClick={onReload}><RefreshCw size={15}/></button>
          </div>
          <div className="message-list">
            {messages.map(m => (
              <div className="message-row" key={m._id}>
                <div className="mentor-avatar tiny">{initials(m.sender?.name)}</div>
                <div>
                  <strong>{m.sender?.name} → {m.recipient?.name}</strong>
                  <span>{m.subject || 'Message'}</span>
                  <p>{m.message}</p>
                </div>
              </div>
            ))}
            {!messages.length && <Empty text="No messages yet." />}
          </div>
        </section>
      </div>
    </div>
  );
}

function AnnouncementsPage({ students, mentorName, selectedMentorId, isAdmin }) {
  const [ids, setIds] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');

  const send = async () => {
    if (!ids.length || !message.trim()) return toast.error('Select at least one student and enter a message');
    try {
      await sendMentorAnnouncement({
        studentIds: ids,
        title,
        message,
        mentorId: selectedMentorId
      });
      toast.success(`Announcement sent to ${ids.length} students`);
      setMessage('');
      setTitle('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to send announcement');
    }
  };

  return (
    <div>
      <PageActions
        title={`Announcements (${mentorName || 'Mentor'})`}
        subtitle={`Broadcast announcements to students assigned to ${mentorName || 'this mentor'}.`}
      />
      <section className="mentor-card">
        <div className="select-all-row">
          <button
            className="outline-btn"
            onClick={() => setIds(ids.length === students.length ? [] : students.map(s => s.id))}
          >
            {ids.length === students.length ? 'Clear All' : 'Select All'}
          </button>
          <span>{ids.length} selected</span>
        </div>

        <div className="student-select-grid">
          {students.map(s => (
            <label key={s.id} className={ids.includes(s.id) ? 'selected' : ''}>
              <input
                type="checkbox"
                checked={ids.includes(s.id)}
                onChange={e => setIds(e.target.checked ? [...ids, s.id] : ids.filter(x => x !== s.id))}
              />
              <div className="mentor-avatar tiny">{initials(s.name)}</div>
              <span>{s.name}<small>{s.batch}</small></span>
            </label>
          ))}
        </div>

        <div className="form-grid announcement-form">
          <label>
            Title
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Important announcement title"/>
          </label>
          <label className="span-2">
            Message
            <textarea rows="5" value={message} onChange={e => setMessage(e.target.value)} placeholder="Write the announcement message..."/>
          </label>
        </div>

        <button className="primary-btn" onClick={send}><Megaphone size={15}/> Send Announcement</button>
      </section>
    </div>
  );
}

function NotificationsPage({ notifications, onReload, selectedMentorId }) {
  const [markingAll, setMarkingAll] = useState(false);

  const markOne = async n => {
    try {
      await markMentorNotificationRead(n._id);
      onReload();
    } catch (e) {
      console.error('Mark notification read failed:', e);
    }
  };

  const markAll = async () => {
    setMarkingAll(true);
    try {
      await markAllMentorNotificationsRead(selectedMentorId ? { mentorId: selectedMentorId } : {});
      toast.success('All notifications marked as read');
      onReload();
    } catch (e) {
      console.error('Mark all notifications read error:', e);
      toast.error(e.response?.data?.message || 'Failed to mark notifications as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.readAt).length;

  return (
    <div>
      <div className="mentor-page-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span className="eyebrow">MENTOR WORKSPACE</span>
          <h2>Notifications ({notifications.length})</h2>
          <p>Recent assignment submissions, class reminders, student actions and alerts (auto-cleared after 7 days).</p>
        </div>
        {unreadCount > 0 && (
          <button
            type="button"
            className="primary-btn"
            onClick={markAll}
            disabled={markingAll}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '.8rem', padding: '7px 14px' }}
          >
            <CheckCircle2 size={15}/> {markingAll ? 'Marking...' : `Mark All as Read (${unreadCount})`}
          </button>
        )}
      </div>

      <section className="mentor-card" style={{ marginTop: '12px' }}>
        <div className="notification-list">
          {notifications.map(n => {
            const isUnread = !n.readAt;
            return (
              <button
                key={n._id}
                className={`notification-row ${isUnread ? 'unread' : ''}`}
                onClick={() => markOne(n)}
                style={{
                  background: isUnread ? '#fffdf5' : '#ffffff',
                  borderLeft: isUnread ? '4px solid #e8a820' : '4px solid transparent'
                }}
              >
                <div className="notification-icon" style={{ background: isUnread ? '#fef3c7' : '#f1f5f9', color: isUnread ? '#b45309' : '#64748b' }}>
                  <Bell size={16}/>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <strong style={{ color: isUnread ? '#0f172a' : '#475569' }}>{n.title}</strong>
                    {isUnread && (
                      <span style={{ fontSize: '.65rem', fontWeight: 800, background: '#e8a820', color: '#12233f', padding: '1px 6px', borderRadius: '10px' }}>
                        NEW
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '3px 0', color: '#64748b', fontSize: '.8rem' }}>{n.message}</p>
                  <small style={{ color: '#94a3b8', fontSize: '.72rem' }}>{fmtDate(n.createdAt)}</small>
                </div>
                {isUnread && <span className="unread-dot"/>}
              </button>
            );
          })}
          {!notifications.length && (
            <Empty text="No notifications in the last 7 days." />
          )}
        </div>
      </section>
    </div>
  );
}

function ReportsPage({ reports: initialReports, students = [], mentorName, selectedMentorId }) {
  const [reports, setReports] = useState(initialReports || null);
  const [loading, setLoading] = useState(false);

  const fetchLiveReports = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getMentorReports(selectedMentorId ? { mentorId: selectedMentorId } : {});
      if (res.data?.data) {
        setReports(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load mentor reports:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedMentorId]);

  useEffect(() => {
    fetchLiveReports();
  }, [fetchLiveReports]);

  // Compute calculated metrics dynamically from students if reports payload is pending
  const dynamicTotalStudents = reports?.totalStudents ?? students.length ?? 0;
  const dynamicAttendanceRate = reports?.attendanceRate !== undefined
    ? reports.attendanceRate
    : (students.length ? Math.round(students.reduce((acc, s) => acc + (s.attendanceRate ?? s.attendance ?? 0), 0) / students.length) : 0);
  const dynamicAverageScore = reports?.averageScore ?? 0;
  const dynamicStudyHours = reports?.totalStudyHours ?? (students.length ? Math.round(students.reduce((acc, s) => acc + (s.progress || 0) * 0.1, 0) * 10) / 10 : 0);
  const dynamicProjects = reports?.projects ?? 0;
  const dynamicCompletedProjects = reports?.completedProjects ?? 0;
  const dynamicReviewed = reports?.assignmentsReviewed ?? 0;

  const exportCsv = () => {
    const rows = [
      ['Metric', 'Value'],
      ['Total Students', dynamicTotalStudents],
      ['Attendance Rate', `${dynamicAttendanceRate}%`],
      ['Average Score', `${dynamicAverageScore}%`],
      ['Study Hours', `${dynamicStudyHours} hrs`],
      ['Projects', dynamicProjects],
      ['Completed Projects', dynamicCompletedProjects],
      ['Assignments Reviewed', dynamicReviewed],
      [],
      ['Student Name', 'Email', 'Domain', 'Attendance Rate', 'Progress', 'Status']
    ];
    students.forEach(s => {
      rows.push([
        s.name || '—',
        s.email || '—',
        s.domain || 'General',
        `${(s.attendanceRate ?? s.attendance ?? 0)}%`,
        `${s.progress || 0}%`,
        s.status || 'Active'
      ]);
    });
    const csv = rows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `weintern-mentor-report-${mentorName || 'mentor'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mentor-page-title">
        <div>
          <span className="eyebrow">MENTOR WORKSPACE</span>
          <h2>Mentor Reports ({mentorName || 'Mentor'})</h2>
          <p>Real-time performance analytics, student attendance, assignments and project progress.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="outline-btn" onClick={fetchLiveReports} disabled={loading}>
            🔄 {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button className="outline-btn" onClick={exportCsv}><FileText size={15}/> Export CSV</button>
          <button className="primary-btn" onClick={() => window.print()}><FileText size={15}/> Print / Save PDF</button>
        </div>
      </div>

      <div className="report-grid">
        {[
          ['Total Students', dynamicTotalStudents],
          ['Attendance Rate', `${dynamicAttendanceRate}%`],
          ['Average Score', `${dynamicAverageScore}%`],
          ['Study Hours', `${dynamicStudyHours} hrs`],
          ['Projects Assigned', dynamicProjects],
          ['Completed Projects', dynamicCompletedProjects],
          ['Assignments Reviewed', dynamicReviewed]
        ].map(([l, v]) => (
          <div className="report-card" key={l}>
            <span>{l}</span>
            <strong>{v}</strong>
          </div>
        ))}
      </div>

      {/* Dynamic Student Performance Breakdown */}
      <section className="mentor-card" style={{ marginTop: '18px' }}>
        <div className="mentor-card-head">
          <div>
            <h3>Allocated Student Performance Breakdown ({students.length})</h3>
            <p>Live MongoDB attendance, submission status, and learning progress for this mentor's cohort.</p>
          </div>
        </div>

        {students.length === 0 ? (
          <div style={{ padding: '30px', textAlign: 'center', color: '#64748b', fontSize: '.84rem' }}>
            No students allocated to this mentor cohort yet.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="mentor-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Domain / Batch</th>
                  <th>Attendance Rate</th>
                  <th>Progress</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {students.map(s => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                        <div className="mentor-avatar tiny">{initials(s.name)}</div>
                        <div>
                          <strong style={{ display: 'block', color: '#1e293b', fontSize: '.8rem' }}>{s.name}</strong>
                          <span style={{ fontSize: '.68rem', color: '#64748b' }}>{s.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <strong style={{ fontSize: '.78rem', color: '#1e293b' }}>{s.domain || 'General'}</strong>
                      <span style={{ display: 'block', fontSize: '.68rem', color: '#64748b' }}>{s.batch || 'Batch 2026'}</span>
                    </td>
                    <td>
                      <span style={{
                        fontSize: '.75rem',
                        fontWeight: 700,
                        color: (s.attendanceRate ?? s.attendance ?? 0) >= 75 ? '#166534' : '#dc2626'
                      }}>
                        {s.attendanceRate ?? s.attendance ?? 0}%
                      </span>
                    </td>
                    <td>
                      <div style={{ minWidth: 120 }}>
                        <ProgressBar value={s.progress || 0}/>
                        <span style={{ fontSize: '.66rem', color: '#64748b', marginTop: 3, display: 'block' }}>{s.progress || 0}% complete</span>
                      </div>
                    </td>
                    <td>
                      <span className={`risk-badge ${s.status === 'At Risk' ? 'at-risk' : s.status === 'Excellent' ? 'excellent' : 'on-track'}`}>
                        {s.status || 'Active'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function ProfilePage({ mentor, selectedMentorId, isAdmin, onReload }) {
  const [form, setForm] = useState({
    name: mentor.name || '',
    phone: mentor.phone || '',
    experience: mentor.experience || '',
    bio: mentor.bio || '',
    expertise: (mentor.expertise || []).join(', '),
    skills: (mentor.skills || []).join(', '),
    assignedCourses: (mentor.assignedCourses || []).join(', '),
    assignedBatches: (mentor.assignedBatches || []).join(', ')
  });

  useEffect(() => {
    setForm({
      name: mentor.name || '',
      phone: mentor.phone || '',
      experience: mentor.experience || '',
      bio: mentor.bio || '',
      expertise: (mentor.expertise || []).join(', '),
      skills: (mentor.skills || []).join(', '),
      assignedCourses: (mentor.assignedCourses || []).join(', '),
      assignedBatches: (mentor.assignedBatches || []).join(', ')
    });
  }, [mentor]);

  const [saving, setSaving] = useState(false);
  const update = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateMentorProfile({
        ...form,
        expertise: form.expertise.split(',').map(x => x.trim()).filter(Boolean),
        skills: form.skills.split(',').map(x => x.trim()).filter(Boolean),
        assignedCourses: form.assignedCourses.split(',').map(x => x.trim()).filter(Boolean),
        assignedBatches: form.assignedBatches.split(',').map(x => x.trim()).filter(Boolean),
        mentorId: selectedMentorId
      });
      if (!isAdmin) {
        localStorage.setItem('wi_user', JSON.stringify(res.data?.data));
      }
      toast.success('Mentor profile updated');
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageActions
        title="Mentor Profile"
        subtitle="Professional details, expertise and assigned courses/batches."
      />
      <section className="mentor-card profile-card">
        <div className="profile-hero">
          <div className="mentor-avatar large">{initials(mentor.name)}</div>
          <div>
            <h2>{mentor.name}</h2>
            <p>{mentor.email}</p>
            <span className="role-chip">{isAdmin ? 'Supervised Mentor Profile' : 'Mentor'}</span>
          </div>
        </div>
        <div className="profile-edit-grid">
          <label>Full Name<input value={form.name} onChange={e => update('name', e.target.value)}/></label>
          <label>Phone<input value={form.phone} onChange={e => update('phone', e.target.value)}/></label>
          <label>Experience<input value={form.experience} onChange={e => update('experience', e.target.value)} placeholder="e.g. 5 years"/></label>
          <label>Expertise<input value={form.expertise} onChange={e => update('expertise', e.target.value)} placeholder="e.g. Full Stack, AI"/></label>
          <label className="span-2">Skills<input value={form.skills} onChange={e => update('skills', e.target.value)} placeholder="React, Node.js, Python, MongoDB"/></label>
          <label>Assigned Courses<input value={form.assignedCourses} onChange={e => update('assignedCourses', e.target.value)} placeholder="Web Development, Data Science"/></label>
          <label>Assigned Batches<input value={form.assignedBatches} onChange={e => update('assignedBatches', e.target.value)} placeholder="Batch 2026-A"/></label>
          <label className="span-2">Bio<textarea rows="4" value={form.bio} onChange={e => update('bio', e.target.value)} placeholder="Short professional bio"/></label>
        </div>
        <div className="modal-actions">
          <button className="primary-btn" onClick={save} disabled={saving}>
            <Save size={15}/> {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </section>
    </div>
  );
}

function SettingsPage({ logout, isAdmin }) {
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirm: '' });

  const savePassword = async () => {
    if (!passwords.currentPassword || !passwords.newPassword) return toast.error('Enter current and new password');
    if (passwords.newPassword.length < 6) return toast.error('New password must be at least 6 characters');
    if (passwords.newPassword !== passwords.confirm) return toast.error('Passwords do not match');
    try {
      await changePassword({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      setPasswords({ currentPassword: '', newPassword: '', confirm: '' });
      toast.success('Password changed successfully');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to change password');
    }
  };

  return (
    <div>
      <PageActions title="Settings" subtitle="Account security and notification preferences."/>
      <section className="mentor-card settings-list">
        <div><strong>Email notifications</strong><span>Receive important student, class and assignment alerts.</span></div>
        <label className="switch"><input type="checkbox" defaultChecked/><i/></label>
        <div><strong>Class reminders</strong><span>Receive reminders before scheduled classes.</span></div>
        <label className="switch"><input type="checkbox" defaultChecked/><i/></label>
        <div><strong>Student risk alerts</strong><span>Notify when student activity or attendance drops.</span></div>
        <label className="switch"><input type="checkbox" defaultChecked/><i/></label>
      </section>

      <section className="mentor-card password-card">
        <h3>Change Password</h3>
        <p>Use your current password to set a new password.</p>
        <div className="form-grid">
          <label>Current Password<input type="password" value={passwords.currentPassword} onChange={e => setPasswords(p => ({ ...p, currentPassword: e.target.value }))}/></label>
          <label>New Password<input type="password" value={passwords.newPassword} onChange={e => setPasswords(p => ({ ...p, newPassword: e.target.value }))}/></label>
          <label>Confirm Password<input type="password" value={passwords.confirm} onChange={e => setPasswords(p => ({ ...p, confirm: e.target.value }))}/></label>
        </div>
        <button className="primary-btn" onClick={savePassword}><Save size={15}/> Change Password</button>
      </section>

      {!isAdmin && <button className="danger-btn" onClick={logout}><LogOut size={16}/> Logout</button>}
    </div>
  );
}

function StudentModal({ student, detail, onClose, onRefresh }) {
  const [note, setNote] = useState('');

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await addMentorNote(student.id, { note });
      setNote('');
      toast.success('Private note saved');
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to save note');
    }
  };

  return (
    <Modal title={student.name} onClose={onClose}>
      <div className="student-profile-head">
        <div className="mentor-avatar large">{initials(student.name)}</div>
        <div>
          <h2>{student.name}</h2>
          <p>{student.email} · {student.batch} · {student.course}</p>
          <div className="student-dates">
            <span>Start: {fmtDate(detail?.student?.startDate)}</span>
            <span>Expected completion: {fmtDate(detail?.student?.expectedCompletionDate)}</span>
          </div>
          <span className={`risk-badge ${student.status.toLowerCase().replace(' ', '-')}`}>
            {student.status}
          </span>
        </div>
      </div>

      {!detail ? (
        <div className="mentor-loading small"><div className="mentor-spinner"/></div>
      ) : (
        <>
          <div className="student-metrics">
            {[
              ['Progress', detail.metrics.overallProgress + '%'],
              ['Attendance', detail.metrics.attendance + '%'],
              ['Assignments', detail.metrics.assignmentCompletion + '%'],
              ['Project', detail.metrics.projectCompletion + '%'],
              ['Avg Score', detail.metrics.assessmentPerformance + '%'],
              ['Study', Math.round(detail.metrics.totalStudyMinutes / 60 * 10) / 10 + 'h']
            ].map(([l, v]) => (
              <div key={l}><strong>{v}</strong><span>{l}</span></div>
            ))}
          </div>

          <div className="detail-section">
            <h3>Learning Progress</h3>
            {detail.learningProgress.map(x => (
              <div className="learning-row" key={x.module}>
                <span>{x.module}</span>
                <ProgressBar value={x.progress}/>
                <b>{x.progress}%</b>
              </div>
            ))}
          </div>

          <div className="detail-section">
            <h3>Attendance Overview</h3>
            <div className="attendance-summary">
              <div className="attendance-ring" style={{ '--p': `${detail.metrics.attendance}%` }}>
                <strong>{detail.metrics.attendance}%</strong>
                <span>Attendance</span>
              </div>
              <div className="attendance-box">
                <b>{detail.metrics.classesAttended}</b><span>Present/Late</span>
                <b>{detail.metrics.classesMissed}</b><span>Absent</span>
                <b>{detail.metrics.classesTotal}</b><span>Total Classes</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>Assignments & Submissions</h3>
            <div className="history-list">
              {detail.submissions.slice(0, 8).map(s => (
                <div key={s._id}>
                  <span>{s.assignment?.title || 'Assignment'}</span>
                  <span>{s.score ?? '—'}</span>
                  <span className={`status-pill ${s.status}`}>{s.status}</span>
                </div>
              ))}
              {!detail.submissions.length && <Empty text="No submissions yet." />}
            </div>
          </div>

          <div className="detail-section">
            <h3>Private Mentor Notes</h3>
            <textarea
              rows="3"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Add a note about this student's progress..."
            />
            <button className="primary-btn" onClick={addNote}><Save size={15}/> Save Note</button>
            {detail.notes?.slice(0, 5).map(n => (
              <div className="note-item" key={n._id}>
                {n.note}
                <small>{fmtDate(n.createdAt)}</small>
              </div>
            ))}
          </div>
        </>
      )}
    </Modal>
  );
}

function ClassModal({ students, selectedMentorId, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', description: '', batch: '', course: '',
    date: new Date().toISOString().slice(0, 10),
    startTime: '10:00', endTime: '11:00',
    classType: 'lecture', mode: 'online',
    meetingLink: '', learningMaterialUrl: '', notes: '',
    studentIds: []
  });

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleAllStudents = () => {
    const allIds = students.map(s => String(s._id || s.id));
    if (form.studentIds.length === allIds.length) {
      update('studentIds', []);
    } else {
      update('studentIds', allIds);
    }
  };

  const submit = async () => {
    if (!form.title?.trim() || !form.date) return toast.error('Title and date are required');
    const cleanLink = formatMeetingUrl(form.meetingLink);

    try {
      await createMentorClass({
        ...form,
        meetingLink: cleanLink,
        mentorId: selectedMentorId
      });
      toast.success('Live class scheduled successfully! Both you and your students can now join using the meeting link.');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to schedule class');
    }
  };

  return (
    <Modal title="Schedule a Class / Live Session" onClose={onClose}>
      <div className="form-grid">
        <label>Class Title *<input value={form.title} onChange={e => update('title', e.target.value)} placeholder="e.g. Python Operators & Control Flow" required/></label>
        <label>Batch<input value={form.batch} onChange={e => update('batch', e.target.value)} placeholder="e.g. Python-Aug-2026"/></label>
        <label>Course / Domain<input value={form.course} onChange={e => update('course', e.target.value)} placeholder="e.g. Python Development"/></label>
        <label>Date *<input type="date" value={form.date} onChange={e => update('date', e.target.value)}/></label>
        <label>Start Time *<input type="time" value={form.startTime} onChange={e => update('startTime', e.target.value)}/></label>
        <label>End Time *<input type="time" value={form.endTime} onChange={e => update('endTime', e.target.value)}/></label>
        <label>
          Class Type
          <select value={form.classType} onChange={e => update('classType', e.target.value)}>
            {['lecture', 'practical', 'workshop', 'doubt_session', 'project_review', 'one_to_one', 'assessment'].map(x => (
              <option key={x} value={x}>{x.replace('_', ' ').toUpperCase()}</option>
            ))}
          </select>
        </label>
        <label>
          Mode
          <select value={form.mode} onChange={e => update('mode', e.target.value)}>
            <option value="online">Online (Google Meet / Zoom)</option>
            <option value="offline">Offline</option>
          </select>
        </label>
        <label className="span-2">
          Meeting Link (Google Meet / Zoom)
          <input
            value={form.meetingLink}
            onChange={e => update('meetingLink', e.target.value)}
            placeholder="e.g. meet.google.com/abc-defg-hij or https://meet.google.com/..."
          />
          <small style={{ color: '#64748b', fontSize: '.72rem', marginTop: '3px', display: 'block' }}>
            💡 Tip: When you paste a link (e.g. <code>meet.google.com/abc-defg-hij</code>), both you and allocated students can join directly in a new tab.
          </small>
        </label>
        <label className="span-2">Notes / Agenda<textarea value={form.notes} onChange={e => update('notes', e.target.value)} rows="2" placeholder="Topics covered, prerequisites, etc."/></label>
        <div className="span-2">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <strong className="form-heading" style={{ margin: 0 }}>
              Students ({form.studentIds.length === 0 ? `All ${students.length} Allocated Students` : `${form.studentIds.length} Selected`})
            </strong>
            {students.length > 0 && (
              <button
                type="button"
                onClick={toggleAllStudents}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1d4ed8',
                  fontSize: '.75rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  padding: 0
                }}
              >
                {form.studentIds.length === students.length ? 'Deselect All' : 'Select All'}
              </button>
            )}
          </div>
          {students.length === 0 ? (
            <p style={{ fontSize: '.78rem', color: '#64748b', margin: '4px 0' }}>
              No students currently assigned to this mentor. The class will be visible to any student allocated to you.
            </p>
          ) : (
            <div className="checkbox-grid">
              {students.map(s => {
                const sId = String(s._id || s.id);
                return (
                  <label key={sId}>
                    <input
                      type="checkbox"
                      checked={form.studentIds.includes(sId)}
                      onChange={e => update('studentIds', e.target.checked ? [...form.studentIds, sId] : form.studentIds.filter(x => x !== sId))}
                    />
                    {s.name}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="modal-actions">
        <button className="outline-btn" onClick={onClose}>Cancel</button>
        <button className="primary-btn" onClick={submit}><CalendarDays size={15}/> Schedule Class</button>
      </div>
    </Modal>
  );
}

function AssignmentModal({ students, selectedMentorId, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: '', description: '', batch: '', course: '',
    dueDate: '', maxScore: 100, studentIds: [],
    attachments: [] // Changed to array: [{url: '', name: ''}]
  });
  const [uploading, setUploading] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    setUploading(true);
    const uploadedFiles = [];
    
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await uploadMentorFile(formData);
        uploadedFiles.push({
          url: res.data.fileUrl,
          name: file.name
        });
      }
      
      // Add new files to existing attachments
      update('attachments', [...form.attachments, ...uploadedFiles]);
      toast.success(`Uploaded ${uploadedFiles.length} file(s)`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    update('attachments', form.attachments.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (!form.title || !form.dueDate) return toast.error('Title and due date are required');
    try {
      // Prepare data with attachments array
      const assignmentData = {
        ...form,
        mentorId: selectedMentorId,
        attachmentUrls: form.attachments.map(a => a.url),
        attachmentNames: form.attachments.map(a => a.name)
      };
      await createMentorAssignment(assignmentData);
      toast.success('Assignment created');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to create assignment');
    }
  };

  return (
    <Modal title="Create Assignment" onClose={onClose}>
      <div className="form-grid">
        <label>Assignment Title *<input value={form.title} onChange={e => update('title', e.target.value)} placeholder="e.g. Build Full-Stack REST API"/></label>
        <label>Due Date *<input type="date" value={form.dueDate} onChange={e => update('dueDate', e.target.value)}/></label>
        <label>Batch<input value={form.batch} onChange={e => update('batch', e.target.value)} placeholder="e.g. Batch 2026"/></label>
        <label>Course<input value={form.course} onChange={e => update('course', e.target.value)} placeholder="e.g. Web Development"/></label>
        <label>Max Score<input type="number" value={form.maxScore} onChange={e => update('maxScore', Number(e.target.value))}/></label>
        
        <div className="span-2" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: '.7rem', fontWeight: 800, color: '#516078' }}>
            Attach Problem Statement / Guideline Files (Multiple)
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{
              background: '#f8fafc',
              border: '1.5px dashed #cbd5e1',
              borderRadius: 8,
              padding: '8px 14px',
              cursor: 'pointer',
              fontSize: '.75rem',
              fontWeight: 700,
              color: '#334155',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              width: 'fit-content'
            }}>
              📄 {uploading ? 'Uploading Files...' : 'Upload Files (PDF, DOC, ZIP, Images)'}
              <input 
                type="file" 
                accept=".pdf,.doc,.docx,.zip,.png,.jpg,.jpeg" 
                onChange={handleFileUpload} 
                style={{ display: 'none' }} 
                disabled={uploading}
                multiple
              />
            </label>
            {form.attachments.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.attachments.map((file, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    padding: '6px 10px',
                    background: '#f0fdf4',
                    borderRadius: 6,
                    fontSize: '.75rem'
                  }}>
                    <span style={{ color: '#166534', fontWeight: 700, flex: 1 }}>
                      ✅ {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      style={{
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: 4,
                        padding: '4px 8px',
                        cursor: 'pointer',
                        fontSize: '.7rem',
                        fontWeight: 700,
                        color: '#991b1b'
                      }}
                    >
                      ✕ Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <label className="span-2">Description & Instructions<textarea rows="4" value={form.description} onChange={e => update('description', e.target.value)} placeholder="Enter detailed assignment instructions or submission requirements..."/></label>
        <div className="span-2">
          <strong className="form-heading">Assign to Students</strong>
          <div className="checkbox-grid">
            {students.map(s => {
              const sId = String(s._id || s.id);
              return (
                <label key={sId}>
                  <input
                    type="checkbox"
                    checked={form.studentIds.includes(sId)}
                    onChange={e => update('studentIds', e.target.checked ? [...form.studentIds, sId] : form.studentIds.filter(x => x !== sId))}
                  />
                  {s.name} ({s.domain || 'General'})
                </label>
              );
            })}
          </div>
        </div>
      </div>
      <div className="modal-actions">
        <button className="outline-btn" onClick={onClose}>Cancel</button>
        <button className="primary-btn" onClick={submit}><ClipboardList size={15}/> Create Assignment</button>
      </div>
    </Modal>
  );
}

function ReviewModal({ submission, onClose, onSaved }) {
  const [score, setScore] = useState(submission.score ?? '');
  const [feedback, setFeedback] = useState(submission.feedback || '');
  const [status, setStatus] = useState(submission.status || 'reviewed');

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `http://localhost:5000${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const save = async () => {
    try {
      await reviewMentorSubmission(submission._id, { score: Number(score), feedback, status });
      toast.success('Assignment review saved successfully! 🚀');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to save review');
    }
  };

  // Consolidate all attached files
  const files = [];
  if (Array.isArray(submission.files) && submission.files.length > 0) {
    files.push(...submission.files);
  } else if (Array.isArray(submission.fileUrls) && submission.fileUrls.length > 0) {
    submission.fileUrls.forEach((url, i) => {
      files.push({ name: `Attachment ${i + 1}`, url });
    });
  } else if (submission.fileUrl) {
    files.push({ name: 'Submitted Assignment File', url: submission.fileUrl });
  }

  return (
    <Modal title={`Review — ${submission.assignment?.title || 'Assignment Submission'}`} onClose={onClose}>
      <div className="submission-detail" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Student Header */}
        <div className="person-cell" style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <div className="mentor-avatar">{initials(submission.student?.name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <strong style={{ fontSize: '.92rem', color: '#1e293b' }}>{submission.student?.name}</strong>
            <small style={{ display: 'block', color: '#64748b', fontSize: '.75rem' }}>{submission.student?.email}</small>
          </div>
          {submission.submittedAt && (
            <span style={{ fontSize: '.72rem', color: '#166534', background: '#dcfce7', padding: '3px 9px', borderRadius: '12px', fontWeight: 700 }}>
              Submitted: {fmtDate(submission.submittedAt)}
            </span>
          )}
        </div>

        {/* Student Written Notes / Explanation */}
        {submission.answer && (
          <div style={{ background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <strong style={{ display: 'block', fontSize: '.75rem', color: '#475569', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Student Explanation / Notes:
            </strong>
            <p style={{ margin: 0, fontSize: '.84rem', color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {submission.answer}
            </p>
          </div>
        )}

        {/* GitHub Link */}
        {submission.githubUrl && (
          <a
            href={submission.githubUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#0f172a',
              color: '#ffffff',
              padding: '7px 12px',
              borderRadius: '7px',
              fontSize: '.78rem',
              fontWeight: 700,
              textDecoration: 'none',
              width: 'fit-content'
            }}
          >
            <ExternalLink size={14}/> Open GitHub Repository ↗
          </a>
        )}

        {/* Attached Files & PDFs Section */}
        <div>
          <strong style={{ display: 'block', fontSize: '.78rem', color: '#1e293b', marginBottom: '6px' }}>
            📁 Submitted Assignment Documents & PDFs ({files.length}):
          </strong>

          {files.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {files.map((f, idx) => {
                const fullUrl = resolveUrl(f.url);
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      gap: '8px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                      <span style={{ fontSize: '1.2rem' }}>📄</span>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <span style={{ fontWeight: 700, fontSize: '.8rem', color: '#1e3a8a', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.name || `Attachment ${idx + 1}`}
                        </span>
                        {f.size && (
                          <small style={{ color: '#64748b', fontSize: '.7rem' }}>
                            {Math.round(f.size / 1024)} KB
                          </small>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <a
                        href={fullUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#1d4ed8',
                          color: '#ffffff',
                          border: 'none',
                          padding: '5px 10px',
                          borderRadius: '6px',
                          fontSize: '.74rem',
                          fontWeight: 700,
                          textDecoration: 'none'
                        }}
                      >
                        <ExternalLink size={13}/> View PDF ↗
                      </a>
                      <a
                        href={fullUrl}
                        download
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          background: '#ffffff',
                          color: '#1e40af',
                          border: '1px solid #93c5fd',
                          padding: '5px 10px',
                          borderRadius: '6px',
                          fontSize: '.74rem',
                          fontWeight: 700,
                          textDecoration: 'none'
                        }}
                      >
                        ⬇ Download
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', padding: '10px 12px', borderRadius: '8px', fontSize: '.78rem', color: '#92400e' }}>
              ℹ️ No document or PDF file was attached with this submission.
            </div>
          )}
        </div>

        {/* Score & Feedback Inputs */}
        <label style={{ display: 'block', fontSize: '.8rem', fontWeight: 700, color: '#1e293b' }}>
          Score (Out of {submission.assignment?.maxScore || 100})
          <input
            type="number"
            min="0"
            max="100"
            value={score}
            onChange={e => setScore(e.target.value)}
            placeholder="e.g. 95"
            style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1px solid #cbd5e1', marginTop: '4px' }}
          />
        </label>

        <label style={{ display: 'block', fontSize: '.8rem', fontWeight: 700, color: '#1e293b' }}>
          Feedback & Mentor Review Notes
          <textarea
            rows="4"
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            placeholder="Provide constructive feedback, suggestions or congratulations..."
            style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1px solid #cbd5e1', marginTop: '4px' }}
          />
        </label>

        <label style={{ display: 'block', fontSize: '.8rem', fontWeight: 700, color: '#1e293b' }}>
          Status
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: '1px solid #cbd5e1', marginTop: '4px' }}
          >
            <option value="reviewed">Reviewed</option>
            <option value="approved">Approved</option>
            <option value="changes_requested">Request Changes</option>
          </select>
        </label>
      </div>

      <div className="modal-actions" style={{ marginTop: '16px' }}>
        <button className="outline-btn" onClick={onClose}>Cancel</button>
        <button className="primary-btn" onClick={save}><CheckCircle2 size={15}/> Save Review</button>
      </div>
    </Modal>
  );
}

function ProjectModal({ project, onClose, onSaved }) {
  const [progress, setProgress] = useState(project.progress || 0);
  const [status, setStatus] = useState(project.status || 'assigned');
  const [score, setScore] = useState(project.score !== undefined && project.score !== null ? project.score : '');
  const [comments, setComments] = useState(project.mentorComments || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await updateMentorProject(project._id, {
        progress: Number(progress),
        status,
        score: score !== '' ? Number(score) : undefined,
        mentorComments: comments
      });
      toast.success('Project review saved and updated! 🚀');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Unable to update project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Review Project: ${project.title}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Student Info Card */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#f8fafc', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
          <div className="mentor-avatar tiny">{initials(project.student?.name)}</div>
          <div style={{ flex: 1 }}>
            <strong style={{ display: 'block', fontSize: '.84rem', color: '#1e293b' }}>{project.student?.name || 'Student'}</strong>
            <span style={{ fontSize: '.72rem', color: '#64748b' }}>{project.student?.email}</span>
          </div>
          <span className={`status-pill ${project.status}`}>{project.status || 'assigned'}</span>
        </div>

        {project.description && (
          <div style={{ fontSize: '.78rem', color: '#475569', background: '#f1f5f9', padding: '10px 12px', borderRadius: '8px' }}>
            <strong style={{ display: 'block', color: '#1e293b', marginBottom: '2px' }}>Project Objectives:</strong>
            {project.description}
          </div>
        )}

        {/* Student Submission Showcase */}
        {(project.githubUrl || project.liveDemoUrl || project.studentNotes || project.submittedAt) ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '12px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <strong style={{ fontSize: '.8rem', color: '#166534', display: 'flex', alignItems: 'center', gap: '6px' }}>
                🚀 Student Submission
              </strong>
              {project.submittedAt && (
                <span style={{ fontSize: '.68rem', color: '#15803d' }}>
                  Submitted: {fmtDate(project.submittedAt)}
                </span>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: project.studentNotes ? '8px' : '0' }}>
              {project.githubUrl && (
                <a href={project.githubUrl} target="_blank" rel="noreferrer" className="project-link" style={{ background: '#fff', border: '1px solid #cbd5e1', padding: '4px 10px', borderRadius: '6px', margin: 0 }}>
                  <ExternalLink size={13}/> GitHub Repo
                </a>
              )}
              {project.liveDemoUrl && (
                <a href={project.liveDemoUrl} target="_blank" rel="noreferrer" className="project-link" style={{ background: '#fff', border: '1px solid #bae6fd', color: '#0284c7', padding: '4px 10px', borderRadius: '6px', margin: 0 }}>
                  <ExternalLink size={13}/> Live Demo
                </a>
              )}
            </div>

            {project.studentNotes && (
              <div style={{ fontSize: '.76rem', color: '#14532d', background: '#ffffff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #dcfce7', marginTop: '6px' }}>
                <strong>Student Notes:</strong> {project.studentNotes}
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', padding: '9px 12px', fontSize: '.75rem', color: '#92400e' }}>
            ⏳ Student has not submitted project demo/repo yet.
          </div>
        )}

        {/* Evaluation Controls */}
        <div className="form-grid" style={{ marginTop: '4px' }}>
          <label>
            Progress ({progress}%)
            <input type="range" min="0" max="100" step="5" value={progress} onChange={e => setProgress(e.target.value)}/>
          </label>

          <label>
            Score / Grade (0 - 100)
            <input type="number" min="0" max="100" placeholder="e.g. 95" value={score} onChange={e => setScore(e.target.value)}/>
          </label>

          <label className="span-2">
            Status
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted (Pending Review)</option>
              <option value="changes_requested">Changes Requested</option>
              <option value="reviewed">Reviewed</option>
              <option value="completed">Completed</option>
            </select>
          </label>

          <label className="span-2">
            Mentor Review Feedback & Recommendations
            <textarea rows="4" value={comments} onChange={e => setComments(e.target.value)} placeholder="Provide constructive feedback, code review notes, or congratulations..."/>
          </label>
        </div>

        <div className="modal-actions" style={{ marginTop: '12px' }}>
          <button className="outline-btn" onClick={onClose}>Cancel</button>
          <button className="primary-btn" onClick={save} disabled={saving}>
            <Save size={15}/> {saving ? 'Saving...' : 'Save Review & Grade'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PageActions({ title, subtitle, action, actionText, onAction }) {
  const btnLabel = action || actionText;
  return (
    <div className="mentor-page-title">
      <div>
        <span className="eyebrow">MENTOR WORKSPACE</span>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      {btnLabel && (
        <button className="primary-btn" onClick={onAction}><Plus size={16}/> {btnLabel}</button>
      )}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="mentor-empty">
      <Circle size={18}/>
      <span>{text}</span>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="mentor-modal-backdrop" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="mentor-modal">
        <div className="modal-head">
          <div><h2>{title}</h2></div>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export default MentorDashboard;
